import type { SessioneCantierePublic, TimbraturaPublic } from '@gestionale/shared';
import { oggiItalia } from '@gestionale/shared';
import {
	AlertTriangle,
	BarChart3,
	Building2,
	Camera,
	CheckCircle2,
	ChevronLeft,
	ChevronRight,
	CircleAlert,
	ClipboardList,
	Clock,
	DoorOpen,
	Download,
	FileText,
	Fuel,
	HardHat,
	Home,
	KeyRound,
	Loader2,
	MapPin,
	Package,
	Play,
	Receipt,
	RefreshCw,
	Satellite,
	Search,
	Share2,
	Shield,
	ShieldCheck,
	StopCircle,
	Sunrise,
	Trash2,
	Unlock,
	UserRound,
	X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ToastContainer, type ToastData } from './FeedbackToast';
import { useGeolocation } from './hooks/useGeolocation';
import { useModalFocus } from './hooks/useModalFocus';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import type { BustaPagaMese, GiornoOre } from './lib/api';
import * as api from './lib/api';
import { compressImage } from './lib/compress-image';
import { formatDurationHours } from './lib/format-duration';
import {
	actionDay,
	cacheGiornata,
	projectGiornata,
	readGiornataCache,
} from './lib/offline-giornata';
import * as offlineQueue from './lib/offline-queue';
import { generateQrSvg } from './lib/qr-code';
import { ProfileSection } from './ProfileSection';
import { QueueStatus } from './QueueStatus';

const APP_BUILD = import.meta.env.VITE_APP_BUILD || 'dev';

// ─── Types ───────────────────────────────────────────────────

type Giornata = TimbraturaPublic & { localSummary?: boolean };

interface Cantiere {
	id: string;
	nome: string;
	lat?: number;
	lng?: number;
}

// ─── Cantiere helpers ────────────────────────────────────────

const RECENT_CANTIERI_KEY = 'recent_cantieri';
const MAX_RECENT = 5;

function formatDistanza(m: number): string {
	if (m < 1000) return `${Math.round(m)}m`;
	return `${(m / 1000).toFixed(1)}km`;
}

function getRecentCantieri(): string[] {
	try {
		return JSON.parse(localStorage.getItem(RECENT_CANTIERI_KEY) ?? '[]');
	} catch {
		return [];
	}
}

function saveRecentCantiere(cantiereId: string) {
	const recent = getRecentCantieri().filter((id) => id !== cantiereId);
	recent.unshift(cantiereId);
	localStorage.setItem(RECENT_CANTIERI_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

let toastCounter = 0;

// ─── Helpers ─────────────────────────────────────────────────

function getGreeting(): string {
	const h = new Date().getHours();
	if (h >= 5 && h < 12) return 'Buongiorno';
	if (h >= 12 && h < 18) return 'Buon pomeriggio';
	return 'Buonasera';
}

// ─── App ─────────────────────────────────────────────────────

export function App() {
	const [serverGiornata, setServerGiornata] = useState<Giornata | null>(() =>
		readGiornataCache(api.getQueueOwnerId(), oggiItalia()),
	);
	const [cantieri, setCantieri] = useState<Cantiere[]>([]);
	const [actionLoading, setLoading] = useState(false);
	const [toasts, setToasts] = useState<ToastData[]>([]);
	const [sessionError, setSessionError] = useState<string | null>(null);
	const [showCantiereList, setShowCantiereList] = useState<'entrata' | 'uscita' | null>(null);
	const [showConfirmCantiereNonElenco, setShowConfirmCantiereNonElenco] = useState(false);
	const [showConfirmUscita, setShowConfirmUscita] = useState(false);
	const [pausaDefaultMinuti, setPausaDefaultMinuti] = useState(30);
	const [gpsControlloAbilitato, setGpsControlloAbilitato] = useState(false);
	const [gpsRetentionGiorni, setGpsRetentionGiorni] = useState(10);
	const [showGpsInfo, setShowGpsInfo] = useState(false);
	const [pausaSelezionata, setPausaSelezionata] = useState(30);
	const [pausaPersonalizzata, setPausaPersonalizzata] = useState('');
	const [showRapportino, setShowRapportino] = useState(false);
	const [rapportinoTarget, setRapportinoTarget] = useState<Giornata | null>(null);
	const [pendingRapportino, setPendingRapportino] = useState<Giornata | null>(null);
	const [notaLavoroCtx, setNotaLavoroCtx] = useState<{
		cantiereId?: string;
		sessioneId: string;
		provvisoria: boolean;
		cantiereName: string;
		/** What to do after the nota is confirmed */
		nextAction: 'esci' | 'altro_cantiere' | 'fine_giornata';
	} | null>(null);
	const [activeTab, setActiveTab] = useState<'oggi' | 'ore' | 'documenti' | 'profilo'>('oggi');
	const [documentBusy, setDocumentBusy] = useState(false);
	const [installing, setInstalling] = useState(false);
	const [enablingNotifications, setEnablingNotifications] = useState(false);
	const [notificationMessage, setNotificationMessage] = useState<string | null>(null);
	const [installTipDismissed, setInstallTipDismissed] = useState(() => {
		try {
			return localStorage.getItem('pwa-install-tip-dismissed') === '1';
		} catch {
			return false;
		}
	});
	const [showBuste, setShowBuste] = useState(false);
	const [showTesserino, setShowTesserino] = useState(false);
	const { tryGetPosition, status: gpsStatus } = useGeolocation();
	const isOnline = useOnlineStatus();
	const [queueState, setQueueState] = useState(offlineQueue.readState);
	const [syncing, setSyncing] = useState(false);
	const [archiving, setArchiving] = useState(false);
	const loading = actionLoading || syncing || archiving;
	const actionInFlightRef = useRef(false);
	const lastActionRef = useRef<number>(0);
	const DEBOUNCE_MS = 5000; // 5 second debounce to prevent double taps
	// Track notification permission state for persistent banner
	const [notifPermission, setNotifPermission] = useState<NotificationPermission | 'unsupported'>(
		() => ('Notification' in window ? Notification.permission : 'unsupported'),
	);
	const [pushSubscribed, setPushSubscribed] = useState(false);

	// ─── PWA Install prompt ─────────────────────────────────────
	const [isStandalone, setIsStandalone] = useState(
		() =>
			window.matchMedia('(display-mode: standalone)').matches ||
			(navigator as unknown as { standalone?: boolean }).standalone === true,
	);
	const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<Event | null>(null);
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

	useEffect(() => {
		if (!('serviceWorker' in navigator)) return;

		const hadController = navigator.serviceWorker.controller != null;
		let reloading = false;
		const onControllerChange = () => {
			if (!hadController || reloading) return;
			reloading = true;
			window.location.reload();
		};

		navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
		void navigator.serviceWorker.ready
			.then((registration) => registration.update())
			.catch(() => {
				// Il controllo aggiornamenti non deve impedire l'uso della PWA.
			});

		return () => {
			navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
		};
	}, []);

	useEffect(() => {
		const mql = window.matchMedia('(display-mode: standalone)');
		const onChange = (e: MediaQueryListEvent) => setIsStandalone(e.matches);
		mql.addEventListener('change', onChange);

		const onBeforeInstall = (e: Event) => {
			e.preventDefault();
			setDeferredInstallPrompt(e);
		};
		window.addEventListener('beforeinstallprompt', onBeforeInstall);

		const onInstalled = () => {
			setDeferredInstallPrompt(null);
			setIsStandalone(true);
		};
		window.addEventListener('appinstalled', onInstalled);

		return () => {
			mql.removeEventListener('change', onChange);
			window.removeEventListener('beforeinstallprompt', onBeforeInstall);
			window.removeEventListener('appinstalled', onInstalled);
		};
	}, []);
	// If URL has ?token= or /t/:token path, force re-auth even if there's an old token in localStorage
	const [forceReAuth, setForceReAuth] = useState(() => {
		return (
			new URLSearchParams(window.location.search).has('token') ||
			/^\/t\/.+/.test(window.location.pathname)
		);
	});

	// ─── Startup: recover auth from cookie after PWA install ─
	const [startupRefreshing, setStartupRefreshing] = useState(() => api.hasRefreshTokenOnly());
	useEffect(() => {
		if (!startupRefreshing) return;
		api.tryRefreshToken().then((ok) => {
			setStartupRefreshing(false);
			if (ok) {
				loadGiornata();
				loadCantieri();
				loadTimbratureConfig();
				loadRapportiniMancanti();
			}
		});
	}, [startupRefreshing]);

	// Local date in YYYY-MM-DD format (Europe/Rome timezone, not UTC)
	const oggi = oggiItalia();
	const ownerId = api.getQueueOwnerId();
	const giornata = projectGiornata(serverGiornata, queueState.actions, ownerId, oggi);
	const hasPendingGiornata = queueState.actions.some(
		(action) => action.ownerId === ownerId && actionDay(action.payload.device_timestamp) === oggi,
	);
	const summaryNeedsRefresh = Boolean(serverGiornata?.localSummary);

	// ─── Load state ──────────────────────────────────────────

	const loadGiornata = useCallback(async () => {
		try {
			const res = (await api.getGiornata(oggi)) as { data: Giornata | null };
			setServerGiornata(res.data);
			try {
				cacheGiornata(api.getQueueOwnerId(), oggi, res.data);
			} catch {
				showFeedback(
					'err',
					'Impossibile salvare il riepilogo sul dispositivo. Le timbrature in coda restano separate.',
				);
			}
		} catch (e) {
			if (e instanceof api.SessionExpiredError) {
				setSessionError(e.message);
				return;
			}
			// If token was cleared during refresh failure, redirect to auth
			if (!api.isAuthenticated()) {
				setSessionError('La tua sessione è scaduta. Riclicca il link ricevuto su WhatsApp.');
				return;
			}
			// First day, no timbratura yet — that's ok
		}
	}, [oggi]);

	const loadCantieri = useCallback(async () => {
		try {
			const res = await api.listCantieri();
			setCantieri(res.data);
			// Cache in localStorage for offline use
			try {
				localStorage.setItem('cantieri_cache', JSON.stringify(res.data));
			} catch {
				/* quota */
			}
		} catch (e) {
			if (e instanceof api.SessionExpiredError) {
				setSessionError(e.message);
				return;
			}
			if (!api.isAuthenticated()) {
				setSessionError('La tua sessione è scaduta. Riclicca il link ricevuto su WhatsApp.');
				return;
			}
			// Offline or error — load from localStorage cache
			try {
				const cached = localStorage.getItem('cantieri_cache');
				if (cached) setCantieri(JSON.parse(cached));
			} catch {
				/* corrupt cache */
			}
		}
	}, []);

	const loadTimbratureConfig = useCallback(async () => {
		try {
			const res = await api.getTimbratureConfig();
			setPausaDefaultMinuti(res.data.pausa_default_minuti);
			setPausaSelezionata(res.data.pausa_default_minuti);
			setGpsControlloAbilitato(res.data.gps_controllo_abilitato);
			setGpsRetentionGiorni(res.data.gps_retention_giorni);
		} catch {
			// Mantiene il valore locale se la configurazione non è raggiungibile.
		}
	}, []);

	const loadRapportiniMancanti = useCallback(async () => {
		try {
			const res = await api.listTimbrature({ limit: 30 });
			const rows = res.data as Giornata[];
			const pending = rows.find(
				(t) => t.stato !== 'aperta' && t.rapportino_richiesto && !t.rapportino_ricevuto,
			);
			setPendingRapportino(pending ?? null);
		} catch {
			// Non blocca l'uso della PWA.
		}
	}, []);

	useEffect(() => {
		if (api.isAuthenticated()) {
			loadGiornata();
			loadCantieri();
			loadTimbratureConfig();
			loadRapportiniMancanti();
			// Re-sync existing push subscription to backend (no permission request, safe at startup)
			api
				.resyncPushSubscription()
				.then((synced) => {
					if ('Notification' in window) setNotifPermission(Notification.permission);
					if (synced) {
						setPushSubscribed(true);
						console.log('[App] Push subscription re-synced at startup');
					}
				})
				.catch(() => {});
		}
	}, [loadGiornata, loadCantieri, loadTimbratureConfig, loadRapportiniMancanti]);

	useEffect(() => {
		const onVisibility = () => {
			if (document.visibilityState === 'visible' && api.isAuthenticated()) {
				loadRapportiniMancanti();
			}
		};
		document.addEventListener('visibilitychange', onVisibility);
		return () => document.removeEventListener('visibilitychange', onVisibility);
	}, [loadRapportiniMancanti]);

	useEffect(() => offlineQueue.subscribe(() => setQueueState(offlineQueue.readState())), []);

	function rememberConfirmed(result: offlineQueue.SyncResult) {
		if (!result.confirmedActions.length) return;
		const projected = projectGiornata(
			serverGiornata,
			result.confirmedActions,
			api.getQueueOwnerId(),
			oggi,
		);
		if (!projected) return;
		const confirmed = { ...projected, localSummary: true };
		setServerGiornata(confirmed);
		try {
			cacheGiornata(api.getQueueOwnerId(), oggi, confirmed);
		} catch {
			showFeedback(
				'err',
				'Timbrature confermate, ma il riepilogo locale non è stato salvato. Verifica la giornata online prima di chiudere l’app.',
			);
		}
	}

	async function syncPending() {
		setSyncing(true);
		try {
			const result = await offlineQueue.processQueue();
			rememberConfirmed(result);
			if (result.synced > 0) {
				showFeedback(
					'ok',
					result.synced +
						(result.synced === 1
							? ' timbratura ricevuta dall’ufficio'
							: ' timbrature ricevute dall’ufficio'),
				);
				await loadGiornata();
				await loadRapportiniMancanti();
			}
			if (result.errors.length) showFeedback('err', result.errors[0]!);
			for (const warning of result.warnings) showFeedback('err', warning);
		} catch (error) {
			showFeedback(
				'err',
				error instanceof Error ? error.message : 'Sincronizzazione non completata.',
			);
		} finally {
			setSyncing(false);
			setQueueState(offlineQueue.readState());
		}
	}

	async function archivePending() {
		setArchiving(true);
		try {
			await offlineQueue.archiveQueue();
			showFeedback(
				'ok',
				'Timbrature archiviate sul telefono. L’archiviazione non le invia né le riprova automaticamente.',
			);
		} catch (error) {
			showFeedback('err', error instanceof Error ? error.message : 'Archiviazione non completata.');
		} finally {
			setArchiving(false);
			setQueueState(offlineQueue.readState());
		}
	}

	useEffect(() => {
		if (isOnline && api.isAuthenticated() && !forceReAuth && !startupRefreshing && !sessionError)
			void syncPending();
	}, [isOnline, forceReAuth, startupRefreshing, sessionError]);

	// Persist every action before transmission, even when the connection appears available.
	async function submitQueuedAction(
		type: offlineQueue.QueuedActionType,
		payload: offlineQueue.QueuedAction['payload'],
		successMessage: string,
	) {
		const existing = offlineQueue.getQueue();
		const last = existing[existing.length - 1];
		if (
			last?.ownerId === api.getQueueOwnerId() &&
			last.type === type &&
			actionDay(last.payload.device_timestamp) === actionDay(payload.device_timestamp) &&
			last.payload.cantiere_id === payload.cantiere_id &&
			last.payload.sessione_id === payload.sessione_id
		) {
			showFeedback(
				'err',
				'Questa timbratura è già salvata. Puoi controllarne l’invio nel riepilogo.',
			);
			return false;
		}
		const action = offlineQueue.enqueue(type, payload);
		setQueueState(offlineQueue.readState());
		if (!navigator.onLine) {
			showFeedback('pending', 'Salvata sul telefono. Verrà inviata appena torna la connessione.');
			return 'saved' as const;
		}
		setSyncing(true);
		try {
			const result = await offlineQueue.processQueue();
			rememberConfirmed(result);
			if (result.confirmedIds.includes(action.id)) {
				showFeedback('ok', successMessage);
				for (const warning of result.warnings) showFeedback('err', warning);
				await loadGiornata();
				return 'confirmed' as const;
			}
			showFeedback(
				result.blocked === 'unconfirmed' || !result.errors.length ? 'pending' : 'err',
				result.blocked === 'unconfirmed' || !result.errors.length
					? 'Salvata sul telefono. L’invio è ancora da confermare: controlla il riepilogo.'
					: (result.errors[0] ?? 'Invio non completato. Controlla il riepilogo.'),
			);
			return result.blocked === 'unconfirmed' || !result.errors.length ? ('saved' as const) : false;
		} finally {
			setSyncing(false);
			setQueueState(offlineQueue.readState());
		}
	}

	// ─── Toast helpers ───────────────────────────────────────

	function showFeedback(type: ToastData['type'], msg: string) {
		const id = ++toastCounter;
		setToasts((prev) => [...prev, { id, type, msg, exiting: false }]);
	}

	const dismissToast = useCallback((id: number) => {
		setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
		setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 300);
	}, []);

	// ─── GPS + Action helper ─────────────────────────────────

	async function getGpsPuntualePerAzione() {
		if (!navigator.onLine || !gpsControlloAbilitato) return null;
		try {
			const config = await api.getTimbratureConfig();
			const abilitato = config.data.gps_controllo_abilitato;
			setGpsControlloAbilitato(abilitato);
			return abilitato ? await tryGetPosition() : null;
		} catch {
			// Fail closed: senza conferma aggiornata dal server non si acquisisce il GPS.
			return null;
		}
	}

	async function doAction(successMsg: string, queueType: offlineQueue.QueuedActionType) {
		const now = Date.now();
		if (actionInFlightRef.current || now - lastActionRef.current < DEBOUNCE_MS) return false;
		lastActionRef.current = now;
		actionInFlightRef.current = true;
		setLoading(true);
		try {
			const payload = {
				device_timestamp: new Date().toISOString(),
				...(await getGpsPuntualePerAzione()),
			};
			return await submitQueuedAction(queueType, payload, successMsg);
		} catch (error) {
			showFeedback(
				'err',
				error instanceof Error ? error.message : 'Timbratura non salvata. Riprova.',
			);
			return false;
		} finally {
			actionInFlightRef.current = false;
			setLoading(false);
		}
	}

	async function doCantiereAction(
		cantiereId: string,
		action: 'entrata' | 'uscita',
		notaLavoro?: string,
	) {
		if (actionInFlightRef.current) return false;
		actionInFlightRef.current = true;
		setShowCantiereList(null);
		setLoading(true);
		try {
			const payload = {
				device_timestamp: new Date().toISOString(),
				cantiere_id: cantiereId,
				...(notaLavoro && { nota_lavoro: notaLavoro }),
				...(await getGpsPuntualePerAzione()),
			};
			const confirmed = await submitQueuedAction(
				action === 'entrata' ? 'entraCantiere' : 'esciCantiere',
				payload,
				action === 'entrata' ? 'Ingresso cantiere registrato' : 'Uscita cantiere registrata',
			);
			if (confirmed && action === 'entrata') saveRecentCantiere(cantiereId);
			return confirmed;
		} catch (error) {
			showFeedback(
				'err',
				error instanceof Error ? error.message : 'Timbratura non salvata. Riprova.',
			);
			return false;
		} finally {
			actionInFlightRef.current = false;
			setLoading(false);
		}
	}

	async function doSessioneProvvisoriaAction(
		action: 'entrata' | 'uscita',
		sessioneId?: string,
		notaLavoro?: string,
	) {
		if (actionInFlightRef.current) return false;
		actionInFlightRef.current = true;
		setShowCantiereList(null);
		setShowConfirmCantiereNonElenco(false);
		setLoading(true);
		try {
			const payload = {
				device_timestamp: new Date().toISOString(),
				...(sessioneId && !sessioneId.startsWith('local:') && { sessione_id: sessioneId }),
				...(notaLavoro && { nota_lavoro: notaLavoro }),
				...(await getGpsPuntualePerAzione()),
			};
			return await submitQueuedAction(
				action === 'entrata' ? 'entraSessioneProvvisoria' : 'esciSessioneProvvisoria',
				payload,
				action === 'entrata'
					? 'Ingresso registrato — il cantiere sarà associato dall’ufficio'
					: 'Uscita registrata',
			);
		} catch (error) {
			showFeedback(
				'err',
				error instanceof Error ? error.message : 'Timbratura non salvata. Riprova.',
			);
			return false;
		} finally {
			actionInFlightRef.current = false;
			setLoading(false);
		}
	}

	// ─── Uscita + rapportino ─────────────────────────────────

	/** Called when the NotaLavoroPrompt is confirmed (or skipped) */
	async function handleNotaLavoroConfirm(nota: string | undefined) {
		if (!notaLavoroCtx) return;
		const { cantiereId, sessioneId, provvisoria, nextAction } = notaLavoroCtx;
		setNotaLavoroCtx(null);

		// First: close the current session with the nota
		const confirmed = provvisoria
			? await doSessioneProvvisoriaAction('uscita', sessioneId, nota)
			: cantiereId
				? await doCantiereAction(cantiereId, 'uscita', nota)
				: false;
		if (!confirmed) return;

		// Then: depending on the next action
		if (nextAction === 'altro_cantiere') {
			setShowCantiereList('entrata');
		} else if (nextAction === 'fine_giornata') {
			setPausaSelezionata(pausaDefaultMinuti);
			setPausaPersonalizzata('');
			setShowConfirmUscita(true);
		}
		// 'esci' → already done (doCantiereAction closed the session)
	}

	/** Show nota prompt before exiting a cantiere */
	function promptNotaLavoro(
		sessione: SessioneCantierePublic,
		cantiereName: string,
		nextAction: 'esci' | 'altro_cantiere' | 'fine_giornata',
	) {
		setNotaLavoroCtx({
			cantiereId: sessione.cantiere_id,
			sessioneId: sessione.id,
			provvisoria: sessione.stato_associazione === 'da_associare',
			cantiereName,
			nextAction,
		});
	}

	async function doUscita(pausaMinuti: number) {
		if (actionInFlightRef.current) return;
		actionInFlightRef.current = true;
		setLoading(true);
		try {
			const payload = {
				device_timestamp: new Date().toISOString(),
				pausa_minuti: pausaMinuti,
				...(await getGpsPuntualePerAzione()),
			};
			const confirmed = await submitQueuedAction('uscita', payload, 'Giornata terminata!');
			if (confirmed !== 'confirmed') return;
			// A failed read after confirmation must never enqueue another exit.
			try {
				const updated = (await api.getGiornata(oggi)) as { data: Giornata | null };
				setServerGiornata(updated.data);
				if (
					updated.data &&
					!updated.data.rapportino_ricevuto &&
					updated.data.rapportino_richiesto !== false
				) {
					setRapportinoTarget(updated.data);
					setShowRapportino(true);
				}
			} catch {
				showFeedback(
					'err',
					'Uscita confermata. Riepilogo non aggiornato: ricarica per verificare il rapportino.',
				);
			}
		} catch (error) {
			showFeedback(
				'err',
				error instanceof Error ? error.message : 'Timbratura non salvata. Riprova.',
			);
		} finally {
			actionInFlightRef.current = false;
			setLoading(false);
		}
	}

	async function riapriGiornata() {
		await doAction('Giornata riaperta!', 'riapriGiornata');
	}

	const isGiornataAperta = giornata?.stato === 'aperta';
	const sessioniAperte = (giornata?.sessioni ?? []).filter(
		(s: SessioneCantierePublic) => !s.ora_uscita,
	);
	const hasOpenSessions = sessioniAperte.length > 0;

	// ─── Auth gate (simple) ──────────────────────────────────

	if (startupRefreshing) {
		return (
			<div className="mx-auto flex h-dvh max-w-[420px] flex-col items-center justify-center px-4">
				<div className="flex items-center gap-2">
					<HardHat className="h-7 w-7 text-primary" />
					<h1 className="text-2xl font-bold text-primary">Ore in Cantiere</h1>
				</div>
				<p className="mt-4 text-center text-muted-foreground">
					<Loader2 className="mr-1 inline h-4 w-4 animate-spin" />
					Accesso in corso...
				</p>
			</div>
		);
	}

	if (!api.isAuthenticated() || sessionError || forceReAuth) {
		return (
			<AuthScreen
				onAuth={() => {
					setForceReAuth(false);
					setSessionError(null);
					loadGiornata();
					loadCantieri();
					loadTimbratureConfig();
				}}
				sessionMessage={sessionError ?? undefined}
			/>
		);
	}

	// ─── Nota lavoro prompt overlay ─────────────────────────

	if (notaLavoroCtx) {
		return (
			<NotaLavoroPrompt
				cantiereId={notaLavoroCtx.cantiereId}
				cantiereName={notaLavoroCtx.cantiereName}
				onConfirm={(nota) => handleNotaLavoroConfirm(nota)}
				onSkip={() => handleNotaLavoroConfirm(undefined)}
			/>
		);
	}

	if (showConfirmCantiereNonElenco) {
		return (
			<div className="mx-auto flex h-dvh max-w-[420px] flex-col px-4 py-4">
				<div className="mt-auto mb-4 rounded-2xl bg-secondary p-6 text-center">
					<CircleAlert className="mx-auto mb-3 h-10 w-10 text-amber-500" />
					<p className="text-lg font-bold text-foreground">Cantiere non in elenco</p>
					<p className="mt-2 text-sm text-muted-foreground">
						Registriamo subito posizione e orario. L’ufficio assocerà il cantiere in seguito.
					</p>
				</div>
				<button
					className="mb-3 w-full rounded-2xl bg-primary px-6 py-4 text-lg font-bold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-50"
					disabled={loading}
					onClick={() => doSessioneProvvisoriaAction('entrata')}
				>
					Inizia timbratura
				</button>
				<button
					className="mb-auto w-full rounded-2xl bg-muted px-6 py-4 text-lg font-bold text-muted-foreground transition-colors active:scale-[0.97]"
					onClick={() => setShowConfirmCantiereNonElenco(false)}
				>
					Annulla
				</button>
			</div>
		);
	}

	// ─── Confirm uscita overlay ─────────────────────────────

	if (showConfirmUscita) {
		const opzioniPausa = Array.from(new Set([0, 30, 45, 60, pausaDefaultMinuti])).sort(
			(a, b) => a - b,
		);
		const pausaEffettiva = pausaSelezionata === -1 ? Number(pausaPersonalizzata) : pausaSelezionata;
		const pausaValida =
			Number.isInteger(pausaEffettiva) && pausaEffettiva >= 0 && pausaEffettiva <= 240;
		return (
			<div className="mx-auto flex h-dvh max-w-[420px] flex-col px-4 py-4">
				<div className="mt-auto mb-4 rounded-2xl bg-secondary p-6 text-center">
					<Clock className="mx-auto mb-3 h-10 w-10 text-primary" />
					<p className="text-lg font-bold text-foreground">Pausa pranzo di oggi</p>
					<p className="mt-1 text-sm text-muted-foreground">
						Seleziona la durata. Verrà sottratta dalle ore lavorate.
					</p>
				</div>
				<div className="mb-4 grid grid-cols-2 gap-2">
					{opzioniPausa.map((minuti) => (
						<button
							key={minuti}
							type="button"
							className={`rounded-xl border-2 px-4 py-3 font-bold ${
								pausaSelezionata === minuti
									? 'border-primary bg-primary/10 text-primary'
									: 'border-border bg-card text-foreground'
							}`}
							onClick={() => {
								setPausaSelezionata(minuti);
								setPausaPersonalizzata('');
							}}
						>
							{minuti === 0 ? 'Nessuna' : `${minuti} min`}
							{minuti === pausaDefaultMinuti && (
								<span className="block text-xs font-medium text-muted-foreground">Predefinita</span>
							)}
						</button>
					))}
					<button
						type="button"
						className={`col-span-2 rounded-xl border-2 px-4 py-3 font-bold ${
							pausaSelezionata === -1
								? 'border-primary bg-primary/10 text-primary'
								: 'border-border bg-card text-foreground'
						}`}
						onClick={() => setPausaSelezionata(-1)}
					>
						Altro
					</button>
				</div>
				{pausaSelezionata === -1 && (
					<div className="mb-4">
						<input
							type="number"
							min={0}
							max={240}
							step={5}
							inputMode="numeric"
							value={pausaPersonalizzata}
							onChange={(e) => setPausaPersonalizzata(e.target.value)}
							placeholder="Minuti di pausa"
							className="w-full rounded-xl border-2 border-border bg-card px-4 py-3 text-center text-lg font-semibold"
						/>
					</div>
				)}
				<button
					className="mb-3 w-full rounded-2xl border border-destructive/30 bg-card px-6 py-4 text-lg font-semibold text-destructive transition-colors active:scale-[0.97] disabled:opacity-50"
					disabled={loading || !pausaValida}
					onClick={() => {
						setShowConfirmUscita(false);
						doUscita(pausaEffettiva);
					}}
				>
					<StopCircle className="mr-2 inline h-5 w-5" />
					Termina giornata
				</button>
				<button
					className="mb-auto w-full rounded-2xl bg-muted px-6 py-4 text-lg font-bold text-muted-foreground transition-colors active:scale-[0.97]"
					onClick={() => setShowConfirmUscita(false)}
				>
					Annulla
				</button>
			</div>
		);
	}

	// ─── Rapportino prompt after uscita ──────────────────────

	if (showRapportino) {
		return (
			<RapportinoPrompt
				target={rapportinoTarget}
				onComplete={() => {
					setShowRapportino(false);
					setRapportinoTarget(null);
					showFeedback('ok', '📋 Rapportino caricato!');
					loadGiornata();
					loadRapportiniMancanti();
				}}
				onSkip={() => {
					setShowRapportino(false);
					setRapportinoTarget(null);
					showFeedback('ok', '⏰ Ricordati di caricare il rapportino!');
				}}
			/>
		);
	}

	// ─── Cantiere selection overlay ──────────────────────────

	if (showCantiereList) {
		return (
			<CantiereSelector
				action={showCantiereList}
				cantieri={cantieri}
				sessioniAperte={sessioniAperte}
				loading={loading}
				onSelect={(id) => doCantiereAction(id, showCantiereList)}
				onMissing={() => {
					setShowCantiereList(null);
					setShowConfirmCantiereNonElenco(true);
				}}
				onCancel={() => setShowCantiereList(null)}
				gpsEnabled={gpsControlloAbilitato}
			/>
		);
	}

	// ─── Main UI ─────────────────────────────────────────────

	const userNome = api.getUserNome();
	const recentActions = [
		...(serverGiornata?.ora_inizio
			? [{ label: 'Inizio giornata', time: serverGiornata.ora_inizio, pending: false }]
			: []),
		...(serverGiornata?.ora_fine
			? [{ label: 'Fine giornata', time: serverGiornata.ora_fine, pending: false }]
			: []),
		...(serverGiornata?.sessioni ?? []).flatMap((session) => [
			{ label: 'Ingresso cantiere', time: session.ora_ingresso, pending: false },
			...(session.ora_uscita
				? [{ label: 'Uscita cantiere', time: session.ora_uscita, pending: false }]
				: []),
		]),
		...queueState.actions
			.filter(
				(action) =>
					action.ownerId === ownerId && actionDay(action.payload.device_timestamp) === oggi,
			)
			.map((action) => ({
				label: {
					entrata: 'Inizio giornata',
					uscita: 'Fine giornata',
					riapriGiornata: 'Riapertura giornata',
					entraCantiere: 'Ingresso cantiere',
					esciCantiere: 'Uscita cantiere',
					entraSessioneProvvisoria: 'Ingresso cantiere',
					esciSessioneProvvisoria: 'Uscita cantiere',
				}[action.type],
				time: action.payload.device_timestamp,
				pending: true,
			})),
	]
		.filter((action) => Number.isFinite(Date.parse(action.time)))
		.sort(
			(a, b) => Date.parse(b.time) - Date.parse(a.time) || Number(b.pending) - Number(a.pending),
		);
	const lastAction = recentActions[0];

	async function installApp() {
		if (!deferredInstallPrompt || installing) return;
		setInstalling(true);
		try {
			const prompt = deferredInstallPrompt as unknown as {
				prompt: () => Promise<void>;
				userChoice: Promise<{ outcome: string }>;
			};
			await prompt.prompt();
			await prompt.userChoice;
			setDeferredInstallPrompt(null);
		} catch {
			showFeedback('err', 'Installazione non completata. Puoi riprovare dal menu del browser.');
		} finally {
			setInstalling(false);
		}
	}

	async function enableNotifications() {
		if (enablingNotifications) return;
		setEnablingNotifications(true);
		setNotificationMessage(null);
		try {
			const result = await api.subscribeToPush();
			if ('Notification' in window) setNotifPermission(Notification.permission);
			if (result.ok) {
				setPushSubscribed(true);
				setNotificationMessage('Promemoria attivati.');
			} else {
				setNotificationMessage(
					result.reason === 'denied'
						? 'Puoi attivare le notifiche nelle impostazioni del browser.'
						: 'I promemoria non sono disponibili al momento. Puoi continuare a timbrare e riprovare più tardi.',
				);
			}
		} catch {
			setNotificationMessage('Attivazione non completata. Riprova più tardi.');
		} finally {
			setEnablingNotifications(false);
		}
	}

	return (
		<div className="safe-x mx-auto flex h-dvh max-w-[480px] flex-col overflow-hidden bg-background">
			<header className="safe-top shrink-0 border-b border-border px-4 pb-3 pt-3">
				<div className="flex items-center gap-2">
					<HardHat className="h-6 w-6 shrink-0 text-primary" aria-hidden="true" />
					<h1 className="text-lg font-bold text-primary">Ore in Cantiere</h1>
				</div>
			</header>
			<main className="flex min-h-0 flex-1 flex-col">
				{activeTab === 'oggi' && (
					<section
						aria-label="La giornata di oggi"
						className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4"
					>
						<div>
							<h2 className="break-words text-lg font-semibold">
								{getGreeting()}
								{userNome ? `, ${userNome}` : ''}
							</h2>
							<p className="text-sm text-muted-foreground">{formatDate(oggi)}</p>
						</div>
						<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
							<span className={isOnline ? 'text-muted-foreground' : 'font-medium text-amber-800'}>
								{isOnline ? 'Connesso' : 'Senza connessione'}
							</span>
							{queueState.actions.length > 0 && (
								<a
									href="#timbrature-in-attesa"
									className="min-h-11 inline-flex items-center font-semibold text-amber-800 underline underline-offset-2"
								>
									{queueState.actions.length} in attesa
								</a>
							)}
							{gpsControlloAbilitato && (
								<button
									type="button"
									id="home-gps-info"
									aria-label="Informazioni sul GPS"
									onClick={() => setShowGpsInfo(true)}
									className="inline-flex min-h-11 items-center gap-1.5 text-primary"
								>
									<Satellite className="h-4 w-4" aria-hidden="true" />
									GPS alla timbratura
								</button>
							)}
						</div>
						{gpsStatus === 'loading' && (
							<p role="status" className="text-sm text-primary">
								Acquisizione della posizione…
							</p>
						)}
						{!giornata && (
							<div className="flex flex-col gap-3">
								<div className="rounded-2xl bg-secondary p-4 text-center">
									<Sunrise className="mx-auto mb-2 h-8 w-8 text-amber-400" />
									<p className="text-lg font-bold text-foreground">Giornata non iniziata</p>
									<p className="mt-1 text-sm text-muted-foreground">
										Inizia la giornata, poi scegli il cantiere.
									</p>
								</div>
								<button
									className="w-full rounded-2xl bg-success px-6 py-5 text-xl font-bold text-success-foreground transition-transform active:scale-[0.97] disabled:opacity-50"
									disabled={loading}
									onClick={() => doAction('Giornata iniziata!', 'entrata')}
								>
									<Play className="mr-2 inline h-5 w-5" />
									Inizia giornata
								</button>
							</div>
						)}

						{/* ─── STATE: Giornata aperta, in cantiere ─── */}
						{isGiornataAperta && hasOpenSessions && (
							<div className="flex flex-col gap-3">
								{sessioniAperte.map((s: SessioneCantierePublic) => {
									const provvisoria = s.stato_associazione === 'da_associare';
									const cantiereName = provvisoria
										? 'Cantiere da associare'
										: (cantieri.find((c) => c.id === s.cantiere_id)?.nome ?? 'Cantiere');
									return (
										<div
											key={s.id}
											className={`rounded-2xl border-2 p-4 text-center ${
												provvisoria ? 'border-amber-300 bg-amber-50' : 'border-blue-300 bg-blue-50'
											}`}
										>
											{provvisoria ? (
												<CircleAlert className="mx-auto mb-2 h-8 w-8 text-amber-600" />
											) : (
												<Building2 className="mx-auto mb-2 h-8 w-8 text-primary" />
											)}
											<p
												className={`text-lg font-bold ${provvisoria ? 'text-amber-700' : 'text-primary'}`}
											>
												{cantiereName}
											</p>
											<p
												className={`text-sm font-medium ${provvisoria ? 'text-amber-600' : 'text-blue-500'}`}
											>
												dalle {formatTime(s.ora_ingresso)} — {elapsedSince(s.ora_ingresso)}
											</p>
											{provvisoria && (
												<p className="mt-1 text-xs text-amber-700">
													L’ufficio lo assocerà in seguito
												</p>
											)}
											<button
												className="mt-3 w-full rounded-2xl bg-primary px-4 py-4 text-base font-bold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-50"
												disabled={loading}
												onClick={() => promptNotaLavoro(s, cantiereName, 'esci')}
											>
												<DoorOpen className="mr-2 inline h-5 w-5" />
												Esci dal cantiere
											</button>
										</div>
									);
								})}

								<div className="flex gap-2">
									<button
										className="flex-1 rounded-2xl border border-border bg-card px-3 py-3 text-base font-semibold text-primary transition-transform active:scale-[0.97] disabled:opacity-50"
										disabled={loading}
										onClick={() => {
											const s = sessioniAperte[0]!;
											const nome =
												s.stato_associazione === 'da_associare'
													? 'Cantiere da associare'
													: (cantieri.find((c) => c.id === s.cantiere_id)?.nome ?? 'Cantiere');
											promptNotaLavoro(s, nome, 'altro_cantiere');
										}}
									>
										<Building2 className="mr-1 inline h-4 w-4" />
										Altro cantiere
									</button>
									<button
										className="flex-1 rounded-2xl border border-destructive/30 bg-card px-3 py-3 text-base font-bold text-destructive transition-transform active:scale-[0.97] disabled:opacity-50"
										disabled={loading}
										onClick={() => {
											const s = sessioniAperte[0]!;
											const nome =
												s.stato_associazione === 'da_associare'
													? 'Cantiere da associare'
													: (cantieri.find((c) => c.id === s.cantiere_id)?.nome ?? 'Cantiere');
											promptNotaLavoro(s, nome, 'fine_giornata');
										}}
									>
										<StopCircle className="mr-1 inline h-4 w-4" />
										Fine giornata
									</button>
								</div>
							</div>
						)}

						{/* ─── STATE: Giornata aperta, nessun cantiere ─── */}
						{isGiornataAperta && !hasOpenSessions && (
							<div className="flex flex-col gap-3">
								<div className="rounded-2xl bg-secondary p-4 text-center">
									<CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
									<p className="text-lg font-bold text-foreground">
										{hasPendingGiornata
											? 'Giornata iniziata · in attesa di invio'
											: 'Giornata in corso'}
									</p>
									<p className="text-sm text-muted-foreground">
										dalle {formatTime(giornata?.ora_inizio)}
									</p>
								</div>

								<button
									className="w-full rounded-2xl bg-primary px-6 py-5 text-xl font-bold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-50"
									disabled={loading}
									onClick={() => setShowCantiereList('entrata')}
								>
									<Building2 className="mr-2 inline h-5 w-5" />
									Entra in cantiere
								</button>

								<button
									className="w-full rounded-2xl border border-destructive/30 bg-card px-6 py-4 text-lg font-semibold text-destructive transition-transform active:scale-[0.97] disabled:opacity-50"
									disabled={loading}
									onClick={() => {
										setPausaSelezionata(pausaDefaultMinuti);
										setPausaPersonalizzata('');
										setShowConfirmUscita(true);
									}}
								>
									<StopCircle className="mr-2 inline h-5 w-5" />
									Fine giornata
								</button>
							</div>
						)}

						{/* ─── STATE: Giornata chiusa ─── */}
						{giornata?.stato === 'chiusa' && (
							<div className="flex flex-col gap-3">
								<div className="rounded-2xl bg-secondary p-4 text-center">
									<CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
									<p className="text-lg font-bold text-foreground">
										{hasPendingGiornata
											? 'Fine giornata salvata · in attesa di invio'
											: 'Giornata completata'}
									</p>
									<p className="mt-1 text-2xl font-bold text-foreground">
										{hasPendingGiornata || summaryNeedsRefresh
											? 'Totale ore in attesa di aggiornamento'
											: formatDurationHours(giornata.ore_totali) + ' lavorate'}
									</p>
									{giornata.pausa_minuti > 0 && (
										<p className="mt-1 text-sm text-muted-foreground">
											Pausa sottratta: {giornata.pausa_minuti} minuti
										</p>
									)}
									{giornata.rapportino_ricevuto ? (
										<p className="mt-2 text-sm font-medium text-emerald-600">
											<ClipboardList className="mr-1 inline h-4 w-4" />
											Rapportino caricato
										</p>
									) : hasPendingGiornata || summaryNeedsRefresh ? (
										<p className="mt-2 text-sm text-amber-700">
											Potrai verificare il rapportino quando le timbrature saranno ricevute.
										</p>
									) : giornata.rapportino_richiesto ? (
										<p className="mt-2 text-sm font-medium text-amber-600">
											<AlertTriangle className="mr-1 inline h-4 w-4" />
											Rapportino mancante
										</p>
									) : (
										<p className="mt-2 text-sm font-medium text-muted-foreground">
											<ClipboardList className="mr-1 inline h-4 w-4" />
											Rapportino non richiesto
										</p>
									)}
								</div>
								{!hasPendingGiornata &&
									!summaryNeedsRefresh &&
									giornata.rapportino_richiesto &&
									!giornata.rapportino_ricevuto && (
										<button
											className="w-full rounded-2xl bg-primary px-6 py-4 text-lg font-bold text-primary-foreground transition-transform active:scale-[0.97]"
											onClick={() => {
												setRapportinoTarget(giornata);
												setShowRapportino(true);
											}}
										>
											<Camera className="mr-2 inline h-5 w-5" />
											Carica rapportino
										</button>
									)}
								<button
									className="w-full rounded-2xl border border-border bg-card px-6 py-4 text-lg font-semibold text-primary transition-transform active:scale-[0.97] disabled:opacity-50"
									disabled={loading}
									onClick={riapriGiornata}
								>
									<Unlock className="mr-2 inline h-5 w-5" />
									Riapri giornata
								</button>
								<p className="text-center text-xs text-muted-foreground">
									Disponibile entro 30 min dalla chiusura
								</p>
							</div>
						)}

						{/* ─── STATE: Approvata ─── */}
						{giornata?.stato === 'approvata' && (
							<div className="flex flex-col gap-3">
								<div className="rounded-2xl bg-secondary p-4 text-center">
									<CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-500" />
									<p className="text-lg font-bold text-foreground">Giornata approvata</p>
									<p className="mt-1 text-2xl font-bold text-foreground">
										{formatDurationHours(giornata.ore_totali)} lavorate
									</p>
									{giornata.pausa_minuti > 0 && (
										<p className="mt-1 text-sm text-muted-foreground">
											Pausa: {giornata.pausa_minuti} minuti
										</p>
									)}
								</div>
							</div>
						)}
						{lastAction && (
							<p className="rounded-xl border border-border px-3 py-3 text-sm">
								<span className="block font-semibold">
									Ultima timbratura · {formatTime(lastAction.time)}
								</span>
								<span className="text-muted-foreground">
									{lastAction.label} ·{' '}
									{lastAction.pending
										? 'Salvata sul telefono, in attesa di invio'
										: 'Ricevuta dall’ufficio'}
								</span>
							</p>
						)}
						<div id="timbrature-in-attesa" className="scroll-mt-4">
							<QueueStatus
								actions={queueState.actions}
								archives={queueState.archives}
								storageError={queueState.error}
								syncing={syncing}
								archiving={archiving}
								isOnline={isOnline}
								ownerId={ownerId}
								onRetry={() => void syncPending()}
								onArchive={() => void archivePending()}
							/>
						</div>
						{summaryNeedsRefresh && !hasPendingGiornata && (
							<div role="status" className="rounded-xl bg-blue-50 p-3 text-sm">
								Timbrature ricevute dall’ufficio. Stiamo aggiornando il riepilogo e i totali.{' '}
								<button
									type="button"
									className="min-h-11 font-semibold underline"
									onClick={() => void loadGiornata()}
								>
									Aggiorna riepilogo
								</button>
							</div>
						)}
						{pendingRapportino && pendingRapportino.id !== giornata?.id && (
							<button
								className="mb-3 rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-left text-amber-800"
								onClick={() => {
									setRapportinoTarget(pendingRapportino);
									setShowRapportino(true);
								}}
							>
								<p className="text-sm font-bold">
									<AlertTriangle className="mr-1 inline h-4 w-4" />
									Rapportino mancante del {formatDate(pendingRapportino.data)}
								</p>
								<p className="mt-1 text-xs">Tocca qui per caricare la foto.</p>
							</button>
						)}

						{!isStandalone && !installTipDismissed && (
							<div className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2">
								<button
									type="button"
									className="min-h-11 flex-1 text-left text-sm font-medium text-primary"
									onClick={() => setActiveTab('profilo')}
								>
									Aggiungi l’app al telefono
								</button>
								<button
									type="button"
									aria-label="Nascondi suggerimento di installazione"
									className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground"
									onClick={() => {
										setInstallTipDismissed(true);
										try {
											localStorage.setItem('pwa-install-tip-dismissed', '1');
										} catch {
											/* Optional preference must not block attendance. */
										}
									}}
								>
									<X className="h-5 w-5" aria-hidden="true" />
								</button>
							</div>
						)}
					</section>
				)}
				{activeTab === 'ore' && <MieOreSection />}
				{activeTab === 'documenti' && (
					<DocumentiSection
						cantieri={cantieri}
						sessioniAperte={sessioniAperte}
						onToast={showFeedback}
						onBusyChange={setDocumentBusy}
					/>
				)}
				{activeTab === 'profilo' && (
					<ProfileSection
						name={userNome}
						installed={isStandalone}
						isIOS={isIOS}
						canInstall={Boolean(deferredInstallPrompt)}
						installing={installing}
						onInstall={() => void installApp()}
						notificationPermission={notifPermission}
						pushSubscribed={pushSubscribed}
						enablingNotifications={enablingNotifications}
						notificationMessage={notificationMessage}
						onEnableNotifications={() => void enableNotifications()}
						gpsEnabled={gpsControlloAbilitato}
						onGpsInfo={() => setShowGpsInfo(true)}
						onPayslips={() => setShowBuste(true)}
						onBadge={() => setShowTesserino(true)}
						build={APP_BUILD}
					/>
				)}
			</main>

			{showGpsInfo && (
				<GpsInfoOverlay
					retentionGiorni={gpsRetentionGiorni}
					onClose={() => setShowGpsInfo(false)}
				/>
			)}
			{showBuste && <BustePagaSection onClose={() => setShowBuste(false)} />}
			{showTesserino && <TesserinoScreen onClose={() => setShowTesserino(false)} />}
			<nav
				aria-label="Navigazione principale"
				className="safe-bottom flex shrink-0 gap-1 border-t border-border bg-white px-2 pt-2"
			>
				{(
					[
						{ id: 'oggi', label: 'Oggi', Icon: Home },
						{ id: 'ore', label: 'Ore', Icon: BarChart3 },
						{ id: 'documenti', label: 'Documenti', Icon: FileText },
						{ id: 'profilo', label: 'Profilo', Icon: UserRound },
					] as const
				).map(({ id, label, Icon }) => (
					<button
						key={id}
						type="button"
						aria-current={activeTab === id ? 'page' : undefined}
						disabled={documentBusy && activeTab !== id}
						onClick={() => setActiveTab(id)}
						className={`flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl px-1 text-xs font-semibold disabled:opacity-50 ${activeTab === id ? 'bg-blue-50 text-primary' : 'text-muted-foreground'}`}
					>
						<Icon className="h-5 w-5" aria-hidden="true" />
						{label}
					</button>
				))}
			</nav>
			<ToastContainer toasts={toasts} onDismiss={dismissToast} />
		</div>
	);
}

// ─── Le mie ore section ──────────────────────────────────────

function MieOreSection() {
	const now = new Date();
	const [anno, setAnno] = useState(now.getFullYear());
	const [mese, setMese] = useState(now.getMonth() + 1);
	const [giorni, setGiorni] = useState<GiornoOre[]>([]);
	const [oreTotali, setOreTotali] = useState('0');
	const [giorniLavorati, setGiorniLavorati] = useState(0);
	const [statoRiepilogo, setStatoRiepilogo] = useState<string | null>(null);
	const [oreVisibili, setOreVisibili] = useState(true);
	const [messaggioOre, setMessaggioOre] = useState<string | null>(null);
	const [loadingOre, setLoadingOre] = useState(true);
	const [oreError, setOreError] = useState(false);
	const [oreRetry, setOreRetry] = useState(0);

	const mesiNomi = [
		'Gen',
		'Feb',
		'Mar',
		'Apr',
		'Mag',
		'Giu',
		'Lug',
		'Ago',
		'Set',
		'Ott',
		'Nov',
		'Dic',
	];

	// Prevent navigating to future months
	const isFutureMonth =
		anno > now.getFullYear() || (anno === now.getFullYear() && mese >= now.getMonth() + 1);

	useEffect(() => {
		let current = true;
		setLoadingOre(true);
		setOreError(false);
		api
			.getMieOre(anno, mese)
			.then((res) => {
				if (!current) return;
				setOreVisibili(res.data.visibile);
				setMessaggioOre(res.data.messaggio ?? null);
				setGiorni(res.data.giorni);
				setOreTotali(res.data.ore_totali);
				setGiorniLavorati(res.data.giorni_lavorati);
				setStatoRiepilogo(res.data.stato_riepilogo);
			})
			.catch(() => {
				if (current) setOreError(true);
			})
			.finally(() => {
				if (current) setLoadingOre(false);
			});
		return () => {
			current = false;
		};
	}, [anno, mese, oreRetry]);

	function prevMonth() {
		if (mese === 1) {
			setMese(12);
			setAnno((a) => a - 1);
		} else setMese((m) => m - 1);
	}
	function nextMonth() {
		if (mese === 12) {
			setMese(1);
			setAnno((a) => a + 1);
		} else setMese((m) => m + 1);
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col bg-background">
			{/* Header */}
			<div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
				<h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
					<BarChart3 className="h-5 w-5 text-primary" />
					Le mie ore
				</h2>
			</div>

			{/* Scrollable content */}
			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4">
				{/* Period selector */}
				<div className="mb-4 flex items-center justify-center gap-4">
					<button
						className="rounded-lg border border-border bg-card px-3 py-2 transition-colors active:bg-accent"
						aria-label="Mese precedente"
						onClick={prevMonth}
					>
						<ChevronLeft className="h-4 w-4" />
					</button>
					<span className="text-lg font-semibold">
						{mesiNomi[mese - 1]} {anno}
					</span>
					<button
						className="rounded-lg border border-border bg-card px-3 py-2 transition-colors active:bg-accent disabled:opacity-30"
						aria-label="Mese successivo"
						onClick={nextMonth}
						disabled={isFutureMonth}
					>
						<ChevronRight className="h-4 w-4" />
					</button>
				</div>

				{/* Approval status badge */}
				{statoRiepilogo && (
					<div className="mb-3 text-center">
						<span
							className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
								statoRiepilogo === 'approvato'
									? 'bg-green-100 text-green-800'
									: 'bg-amber-100 text-amber-800'
							}`}
						>
							{statoRiepilogo === 'approvato' ? (
								<>
									<CheckCircle2 className="h-3 w-3" /> Mese approvato
								</>
							) : (
								<>
									<Loader2 className="h-3 w-3" /> In attesa di approvazione
								</>
							)}
						</span>
					</div>
				)}

				{oreError ? (
					<div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
						<p>Non riusciamo a caricare le ore di questo mese.</p>
						<button
							type="button"
							className="mt-2 min-h-11 font-semibold underline"
							onClick={() => setOreRetry((value) => value + 1)}
						>
							Riprova caricamento ore
						</button>
					</div>
				) : !loadingOre && !oreVisibili ? (
					<div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
						<Clock className="mx-auto mb-3 h-10 w-10 text-amber-600" />
						<p className="font-bold text-amber-900">Mese in attesa di approvazione</p>
						<p className="mt-2 text-sm text-amber-800">
							{messaggioOre ?? 'Le ore saranno disponibili dopo l’approvazione dell’ufficio.'}
						</p>
					</div>
				) : loadingOre ? (
					<p className="text-center text-sm text-muted-foreground">Caricamento...</p>
				) : (
					<>
						<div className="mb-4 flex justify-around rounded-lg bg-secondary p-3">
							<div className="text-center">
								<div className="text-2xl font-bold">{formatDurationHours(oreTotali)}</div>
								<div className="text-xs text-muted-foreground">Ore totali</div>
							</div>
							<div className="text-center">
								<div className="text-2xl font-bold">{giorniLavorati}</div>
								<div className="text-xs text-muted-foreground">Giorni lavorati</div>
							</div>
						</div>
						{giorni.length === 0 ? (
							<p className="text-center text-sm text-muted-foreground">Nessuna timbratura</p>
						) : (
							<div className="flex flex-col gap-2">
								{giorni.map((g) => (
									<div
										key={g.data}
										className="flex items-center rounded-lg border border-border bg-card px-3 py-2.5"
									>
										<div className="flex-1">
											<span className="font-semibold">
												{new Date(g.data + 'T00:00:00').toLocaleDateString('it-IT', {
													weekday: 'short',
													day: 'numeric',
												})}
											</span>
										</div>
										<div className="font-mono font-semibold">{formatDurationHours(g.ore)}</div>
										<div className="ml-2">
											{g.auto_chiusa ? (
												<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
													auto
												</span>
											) : g.stato === 'anomala' ? (
												<span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
													anomala
												</span>
											) : g.stato === 'approvata' ? (
												<span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
													<CheckCircle2 className="inline h-3 w-3" />
												</span>
											) : (
												<span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
													ok
												</span>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

// ─── Buste paga section ──────────────────────────────────────

function BustePagaSection({ onClose }: { onClose: () => void }) {
	const modalRef = useModalFocus(onClose);
	const [buste, setBuste] = useState<BustaPagaMese[]>([]);
	const [loadingBuste, setLoadingBuste] = useState(true);
	const [busteError, setBusteError] = useState(false);
	const [busteRetry, setBusteRetry] = useState(0);
	const [downloadError, setDownloadError] = useState(false);

	const mesiNomi = [
		'Gen',
		'Feb',
		'Mar',
		'Apr',
		'Mag',
		'Giu',
		'Lug',
		'Ago',
		'Set',
		'Ott',
		'Nov',
		'Dic',
	];

	useEffect(() => {
		let current = true;
		setLoadingBuste(true);
		setBusteError(false);
		api
			.getBustePagaDisponibili()
			.then((res) => {
				if (current) setBuste(res.data);
			})
			.catch(() => {
				if (current) setBusteError(true);
			})
			.finally(() => {
				if (current) setLoadingBuste(false);
			});
		return () => {
			current = false;
		};
	}, [busteRetry]);

	async function handleDownload(b: BustaPagaMese) {
		setDownloadError(false);
		try {
			const res = await api.getBustaPagaUrl('me', b.anno, b.mese);
			window.open(res.data.url, '_blank');
		} catch {
			setDownloadError(true);
		}
	}

	return (
		<div
			ref={modalRef}
			role="dialog"
			aria-modal="true"
			aria-labelledby="payslips-title"
			tabIndex={-1}
			className="overlay-enter safe-top safe-x safe-bottom fixed inset-0 z-40 flex flex-col bg-background"
		>
			{/* Header */}
			<div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
				<h2
					id="payslips-title"
					className="flex items-center gap-2 text-lg font-bold text-foreground"
				>
					<FileText className="h-5 w-5 text-primary" />
					Buste Paga
				</h2>
				<button
					type="button"
					aria-label="Torna al profilo"
					className="flex h-11 w-11 items-center justify-center rounded-full transition-colors active:bg-accent"
					onClick={onClose}
				>
					<X className="h-5 w-5 text-muted-foreground" />
				</button>
			</div>

			{/* Scrollable content */}
			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4">
				{downloadError && (
					<p role="alert" className="mb-3 rounded-xl bg-amber-50 p-3 text-sm">
						Non riusciamo ad aprire il PDF. Riprova selezionando la busta paga.
					</p>
				)}
				{busteError ? (
					<div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
						<p>Non riusciamo a caricare le buste paga.</p>
						<button
							type="button"
							className="mt-2 min-h-11 font-semibold underline"
							onClick={() => setBusteRetry((value) => value + 1)}
						>
							Riprova caricamento buste paga
						</button>
					</div>
				) : loadingBuste ? (
					<p className="text-center text-sm text-muted-foreground">Caricamento...</p>
				) : buste.length === 0 ? (
					<p className="text-center text-sm text-muted-foreground">
						Nessuna busta paga disponibile
					</p>
				) : (
					<div className="flex flex-col gap-2">
						{buste.map((b) => (
							<button
								key={`${b.anno}-${b.mese}`}
								className="flex items-center rounded-lg border border-border bg-card px-3 py-2.5 transition-colors active:bg-accent"
								onClick={() => handleDownload(b)}
							>
								<span className="flex-1 font-semibold">
									{mesiNomi[b.mese - 1]} {b.anno}
								</span>
								<span className="text-sm font-medium text-primary">Scarica PDF →</span>
							</button>
						))}
					</div>
				)}
			</div>
		</div>
	);
}

// ─── Documenti section ───────────────────────────────────────

function GpsInfoOverlay({
	retentionGiorni,
	onClose,
}: {
	retentionGiorni: number;
	onClose: () => void;
}) {
	const modalRef = useModalFocus(onClose);
	return (
		<div
			ref={modalRef}
			tabIndex={-1}
			className="overlay-enter safe-top safe-x safe-bottom fixed inset-0 z-40 flex flex-col bg-background"
			role="dialog"
			aria-modal="true"
			aria-labelledby="gps-info-title"
		>
			<div className="flex items-center gap-3 border-b border-border px-4 py-4">
				<button
					type="button"
					className="flex h-11 w-11 items-center justify-center rounded-xl text-muted-foreground active:bg-accent"
					onClick={onClose}
					aria-label="Chiudi informativa GPS"
				>
					<ChevronLeft className="h-5 w-5" />
				</button>
				<div>
					<h2 id="gps-info-title" className="text-lg font-bold text-foreground">
						Come viene usato il GPS
					</h2>
					<p className="text-xs text-muted-foreground">Sintesi operativa per le timbrature</p>
				</div>
			</div>

			<div className="flex-1 space-y-4 overflow-y-auto px-4 py-5 text-sm text-foreground">
				<div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-950">
					<div className="mb-2 flex items-center gap-2 font-bold">
						<Satellite className="h-5 w-5" />
						Rilevazione puntuale
					</div>
					<p>
						La posizione viene richiesta solo quando confermi un ingresso o un’uscita della giornata
						o del cantiere. Non viene rilevata in background e non vengono ricostruiti i tuoi
						spostamenti.
					</p>
				</div>

				<section>
					<h3 className="font-bold">Dati utilizzati</h3>
					<p className="mt-1 text-muted-foreground">
						Posizione, accuratezza, orario della rilevazione, azione di timbratura e cantiere. Il
						server può calcolare distanza e stato tecnico della rilevazione.
					</p>
				</section>

				<section>
					<h3 className="font-bold">Conservazione e accessi</h3>
					<p className="mt-1 text-muted-foreground">
						Le coordinate esatte sono cancellate dal database operativo entro{' '}
						<strong>{retentionGiorni} giorni</strong>. Possono essere aperte solo da utenti
						aziendali autorizzati e ogni apertura viene registrata.
					</p>
				</section>

				<section>
					<h3 className="font-bold">Nessuna decisione automatica</h3>
					<p className="mt-1 text-muted-foreground">
						Una posizione assente, imprecisa o lontana è un dato da verificare: la PWA non blocca la
						timbratura e non applica sanzioni automatiche.
					</p>
				</section>

				<section>
					<h3 className="font-bold">Se il GPS non funziona</h3>
					<p className="mt-1 text-muted-foreground">
						Puoi comunque timbrare e selezionare il cantiere manualmente. Per accedere ai tuoi dati
						o segnalare un errore, contatta il datore di lavoro usando il canale indicato
						nell’informativa privacy ricevuta.
					</p>
				</section>

				<p className="rounded-xl bg-muted p-3 text-xs text-muted-foreground">
					Questa sintesi non sostituisce l’informativa completa sul trattamento dei dati personali
					che deve essere consegnata dal datore di lavoro.
				</p>
			</div>

			<div className="border-t border-border p-4">
				<button
					type="button"
					className="w-full rounded-2xl bg-primary px-5 py-3.5 font-bold text-primary-foreground active:scale-[0.98]"
					onClick={onClose}
				>
					Chiudi
				</button>
			</div>
		</div>
	);
}

// ─── Cantiere selector (GPS + recenti + ricerca) ─────────────

function CantiereSelector({
	action,
	cantieri,
	sessioniAperte,
	loading,
	onSelect,
	onMissing,
	onCancel,
	gpsEnabled,
}: {
	action: 'entrata' | 'uscita';
	cantieri: Cantiere[];
	sessioniAperte: SessioneCantierePublic[];
	loading: boolean;
	onSelect: (id: string) => void;
	onMissing: () => void;
	onCancel: () => void;
	gpsEnabled: boolean;
}) {
	const [search, setSearch] = useState('');
	const searchRef = useRef<HTMLInputElement>(null);

	// Base list: for uscita only show cantieri with open sessions
	const baseList =
		action === 'uscita'
			? cantieri.filter((c) => sessioniAperte.some((s) => s.cantiere_id === c.id))
			: cantieri;

	// Filter by search text
	const searchLower = search.toLowerCase().trim();
	const filtered = searchLower
		? baseList.filter((c) => c.nome.toLowerCase().includes(searchLower))
		: baseList;

	// Compute distances
	const withDistance = filtered.map((c) => ({
		...c,
		distanza: null as number | null,
	}));

	// Split into sections for entrata (no sections needed for uscita — usually 1-2 items)
	const recentIds = getRecentCantieri();
	const hasGps = false;

	let sections: { label: string; icon: React.ReactNode; items: typeof withDistance }[];

	if (action === 'uscita' || searchLower) {
		// Uscita or search active: single flat list, sorted by distance or alphabetically
		const sorted = [...withDistance].sort((a, b) =>
			a.distanza != null && b.distanza != null
				? a.distanza - b.distanza
				: a.nome.localeCompare(b.nome),
		);
		sections = [{ label: '', icon: null, items: sorted }];
	} else {
		// Entrata: smart sections
		const nearby: typeof withDistance = [];
		const recent: typeof withDistance = [];
		const rest: typeof withDistance = [];
		const usedIds = new Set<string>();

		// 1. Nearby (GPS): top 3 closest within 5km
		if (hasGps) {
			const byDist = [...withDistance]
				.filter((c) => c.distanza != null && c.distanza < 5000)
				.sort((a, b) => a.distanza! - b.distanza!);
			for (const c of byDist.slice(0, 3)) {
				nearby.push(c);
				usedIds.add(c.id);
			}
		}

		// 2. Recent: cantieri used recently, not already in nearby
		for (const rid of recentIds) {
			if (usedIds.has(rid)) continue;
			const c = withDistance.find((x) => x.id === rid);
			if (c) {
				recent.push(c);
				usedIds.add(c.id);
			}
		}

		// 3. All others, alphabetical
		for (const c of withDistance) {
			if (!usedIds.has(c.id)) rest.push(c);
		}
		rest.sort((a, b) => a.nome.localeCompare(b.nome));

		sections = [];
		if (nearby.length > 0) {
			sections.push({
				label: 'Vicino a te',
				icon: <MapPin className="mr-1.5 inline h-4 w-4" />,
				items: nearby,
			});
		}
		if (recent.length > 0) {
			sections.push({
				label: 'Recenti',
				icon: <Clock className="mr-1.5 inline h-4 w-4" />,
				items: recent,
			});
		}
		if (rest.length > 0) {
			sections.push({
				label: nearby.length > 0 || recent.length > 0 ? 'Tutti' : '',
				icon:
					nearby.length > 0 || recent.length > 0 ? (
						<Building2 className="mr-1.5 inline h-4 w-4" />
					) : null,
				items: rest,
			});
		}
	}

	return (
		<div className="mx-auto flex h-dvh max-w-[420px] flex-col px-4 py-4">
			<h2 className="mb-3 text-center text-xl font-bold text-foreground">
				{action === 'entrata' ? (
					<>
						<Building2 className="mr-2 inline h-5 w-5 text-primary" />
						Scegli cantiere
					</>
				) : (
					<>
						<DoorOpen className="mr-2 inline h-5 w-5 text-warning" />
						Esci da cantiere
					</>
				)}
			</h2>

			{gpsEnabled && (
				<p className="mb-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
					La posizione viene richiesta una sola volta quando confermi l’ingresso o l’uscita. Non è
					attivo alcun tracciamento continuo.
				</p>
			)}

			{/* Search — only for entrata with 6+ cantieri */}
			{action === 'entrata' && baseList.length >= 6 && (
				<div className="relative mb-3">
					<Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
					<input
						ref={searchRef}
						type="text"
						placeholder="Cerca cantiere..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="w-full rounded-xl border-2 border-border bg-card py-3 pl-10 pr-4 text-base text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
					/>
					{search && (
						<button
							type="button"
							className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground active:bg-accent"
							onClick={() => {
								setSearch('');
								searchRef.current?.focus();
							}}
						>
							<X className="h-4 w-4" />
						</button>
					)}
				</div>
			)}

			{/* List */}
			<div className="mb-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
				{sections.map((section) => (
					<div key={section.label || 'flat'}>
						{section.label && (
							<p className="mb-1.5 mt-2 flex items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
								{section.icon}
								{section.label}
							</p>
						)}
						<div className="flex flex-col gap-2">
							{section.items.map((c) => (
								<button
									key={c.id}
									className="flex items-center justify-between rounded-xl border-2 border-border bg-card px-4 py-3.5 text-left transition-colors active:scale-[0.97] active:bg-accent"
									onClick={() => onSelect(c.id)}
									disabled={loading}
								>
									<span className="text-lg font-semibold text-foreground">{c.nome}</span>
									{c.distanza != null && (
										<span className="ml-2 shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
											{formatDistanza(c.distanza)}
										</span>
									)}
								</button>
							))}
						</div>
					</div>
				))}
				{filtered.length === 0 && (
					<p className="py-8 text-center text-muted-foreground">Nessun cantiere trovato</p>
				)}
			</div>

			{action === 'entrata' && (
				<button
					type="button"
					className="mb-3 w-full rounded-2xl border-2 border-amber-300 bg-amber-50 px-5 py-4 text-left transition-transform active:scale-[0.97]"
					onClick={onMissing}
					disabled={loading}
				>
					<span className="flex items-center gap-3">
						<CircleAlert className="h-6 w-6 shrink-0 text-amber-600" />
						<span>
							<span className="block text-base font-bold text-amber-800">
								Cantiere non in elenco
							</span>
							<span className="block text-xs text-amber-700">L’ufficio lo assocerà in seguito</span>
						</span>
					</span>
				</button>
			)}

			<button
				className="w-full rounded-2xl bg-muted px-6 py-4 text-lg font-bold text-muted-foreground transition-colors active:scale-[0.97]"
				onClick={onCancel}
			>
				Annulla
			</button>
		</div>
	);
}

// ─── Nota Lavoro Prompt (shown before closing a session) ─────

export function NotaLavoroPrompt({
	cantiereId,
	cantiereName,
	onConfirm,
	onSkip,
}: {
	cantiereId?: string;
	cantiereName: string;
	onConfirm: (nota: string) => void;
	onSkip: () => void;
}) {
	const [text, setText] = useState('');
	const [recenti, setRecenti] = useState<string[]>([]);
	const [loadingRecenti, setLoadingRecenti] = useState(false);
	const [suggerimenti, setSuggerimenti] = useState<string[]>([]);
	const [loadingSugg, setLoadingSugg] = useState(false);
	const [suggestionsOpen, setSuggestionsOpen] = useState(false);
	const [activeSuggestion, setActiveSuggestion] = useState(-1);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const recentRequestRef = useRef(0);
	const requestRef = useRef(0);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const recentRequestId = ++recentRequestRef.current;
		requestRef.current++;
		setText('');
		setRecenti([]);
		setSuggerimenti([]);
		setLoadingRecenti(Boolean(cantiereId));
		setLoadingSugg(false);
		setSuggestionsOpen(false);
		setActiveSuggestion(-1);

		if (cantiereId) {
			api
				.getSuggerimentiLavoro(cantiereId)
				.then((res) => {
					if (recentRequestRef.current === recentRequestId) {
						setRecenti(res.data.slice(0, 3));
					}
				})
				.catch(() => {
					if (recentRequestRef.current === recentRequestId) setRecenti([]);
				})
				.finally(() => {
					if (recentRequestRef.current === recentRequestId) setLoadingRecenti(false);
				});
		}

		return () => {
			recentRequestRef.current++;
			requestRef.current++;
			if (debounceRef.current) clearTimeout(debounceRef.current);
			if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
		};
	}, [cantiereId]);

	function handleTextChange(val: string) {
		setText(val);
		setActiveSuggestion(-1);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		const query = val.trim();
		const requestId = ++requestRef.current;
		if (!cantiereId || query.length < 2) {
			setSuggerimenti([]);
			setLoadingSugg(false);
			setSuggestionsOpen(false);
			return;
		}

		setSuggestionsOpen(true);
		setLoadingSugg(true);
		debounceRef.current = setTimeout(() => {
			api
				.getSuggerimentiLavoro(cantiereId, query)
				.then((res) => {
					if (requestRef.current === requestId) setSuggerimenti(res.data);
				})
				.catch(() => {
					if (requestRef.current === requestId) setSuggerimenti([]);
				})
				.finally(() => {
					if (requestRef.current === requestId) setLoadingSugg(false);
				});
		}, 300);
	}

	function cancelCloseTimer() {
		if (!closeTimerRef.current) return;
		clearTimeout(closeTimerRef.current);
		closeTimerRef.current = null;
	}

	function closeSuggestionsSoon() {
		cancelCloseTimer();
		closeTimerRef.current = setTimeout(() => {
			setSuggestionsOpen(false);
			setActiveSuggestion(-1);
			closeTimerRef.current = null;
		}, 200);
	}

	function selectSuggestion(suggestion: string) {
		cancelCloseTimer();
		requestRef.current++;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		setText(suggestion);
		setSuggestionsOpen(false);
		setActiveSuggestion(-1);
		inputRef.current?.blur();
	}

	function handleSuggestionKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === 'Escape') {
			setSuggestionsOpen(false);
			setActiveSuggestion(-1);
			return;
		}

		if (suggestionsOpen && suggerimenti.length > 0 && event.key === 'ArrowDown') {
			event.preventDefault();
			setActiveSuggestion((current) => (current + 1) % suggerimenti.length);
			return;
		}

		if (suggestionsOpen && suggerimenti.length > 0 && event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveSuggestion((current) => (current <= 0 ? suggerimenti.length - 1 : current - 1));
			return;
		}

		if (event.key === 'Enter' && activeSuggestion >= 0) {
			const selectedSuggestion = suggerimenti[activeSuggestion];
			if (selectedSuggestion) {
				event.preventDefault();
				selectSuggestion(selectedSuggestion);
			}
			return;
		}

		if (event.key === 'Enter' && text.trim().length >= 3) {
			event.preventDefault();
			setSuggestionsOpen(false);
			setActiveSuggestion(-1);
			inputRef.current?.blur();
		}
	}

	const canConfirm = text.trim().length >= 3;

	return (
		<div className="mx-auto flex h-dvh max-w-[420px] flex-col overflow-y-auto px-4 py-4">
			<div className="mt-auto mb-4 flex shrink-0 flex-col gap-4">
				<div className="rounded-2xl bg-secondary p-5 text-center">
					<ClipboardList className="mx-auto mb-2 h-9 w-9 text-primary" />
					<p className="text-lg font-bold text-foreground">Lavoro svolto</p>
					<p className="mt-1 text-sm text-muted-foreground">{cantiereName}</p>
				</div>

				{/* Description autocomplete */}
				<div className="relative">
					<input
						ref={inputRef}
						type="text"
						value={text}
						onChange={(e) => handleTextChange(e.target.value)}
						onFocus={() => {
							cancelCloseTimer();
							if (text.trim().length >= 2) setSuggestionsOpen(true);
						}}
						onBlur={closeSuggestionsSoon}
						onKeyDown={handleSuggestionKeyDown}
						placeholder="Descrivi il lavoro svolto..."
						maxLength={500}
						className="w-full rounded-xl border border-input bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
						role="combobox"
						aria-autocomplete="list"
						aria-expanded={suggestionsOpen && !loadingSugg && suggerimenti.length > 0}
						aria-controls={
							suggestionsOpen && !loadingSugg && suggerimenti.length > 0
								? 'nota-lavoro-suggestions'
								: undefined
						}
						aria-activedescendant={
							activeSuggestion >= 0 ? `nota-lavoro-suggestion-${activeSuggestion}` : undefined
						}
						autoComplete="off"
						enterKeyHint="done"
					/>

					{text.trim().length === 0 && loadingRecenti && (
						<p className="mt-3 flex items-center gap-2 px-1 text-xs text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							Caricamento delle lavorazioni recenti...
						</p>
					)}

					{text.trim().length === 0 && !loadingRecenti && recenti.length > 0 && (
						<div id="nota-lavoro-recenti" className="mt-3">
							<p className="px-1 text-xs font-medium text-muted-foreground">
								Usate di recente in questo cantiere
							</p>
							<div className="mt-2 grid gap-2">
								{recenti.map((recente) => (
									<button
										key={recente}
										type="button"
										className="w-full shrink-0 rounded-xl border border-border bg-background px-4 py-3 text-left text-sm font-medium transition-colors active:bg-muted"
										onTouchStart={() => selectSuggestion(recente)}
										onPointerDown={(event) => {
											event.preventDefault();
											selectSuggestion(recente);
										}}
										onClick={() => selectSuggestion(recente)}
									>
										{recente}
									</button>
								))}
							</div>
						</div>
					)}

					{text.trim().length > 0 && text.trim().length < 2 && (
						<p className="mt-2 px-1 text-xs text-muted-foreground">
							Scrivi almeno 2 caratteri per cercare tra le lavorazioni di questo cantiere.
						</p>
					)}

					{suggestionsOpen && text.trim().length >= 2 && loadingSugg && (
						<p className="mt-2 flex items-center gap-2 px-1 text-xs text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							Ricerca in questo cantiere...
						</p>
					)}

					{suggestionsOpen &&
						text.trim().length >= 2 &&
						!loadingSugg &&
						suggerimenti.length > 0 && (
							<div
								id="nota-lavoro-suggestions"
								className="mt-2 max-h-40 w-full overflow-y-auto rounded-xl border border-border bg-background py-1 shadow-lg"
								role="listbox"
							>
								{suggerimenti.map((suggestion, index) => (
									<button
										id={`nota-lavoro-suggestion-${index}`}
										key={suggestion}
										type="button"
										role="option"
										aria-selected={activeSuggestion === index}
										className={`block w-full px-4 py-3 text-left text-sm ${
											activeSuggestion === index ? 'bg-secondary' : 'active:bg-muted'
										}`}
										onMouseEnter={() => setActiveSuggestion(index)}
										onTouchStart={() => selectSuggestion(suggestion)}
										onPointerDown={(event) => {
											event.preventDefault();
											selectSuggestion(suggestion);
										}}
										onMouseDown={(event) => event.preventDefault()}
										onClick={() => selectSuggestion(suggestion)}
									>
										{suggestion}
									</button>
								))}
							</div>
						)}

					{suggestionsOpen &&
						text.trim().length >= 2 &&
						!loadingSugg &&
						suggerimenti.length === 0 && (
							<p
								id="nota-lavoro-new-value-hint"
								className="mt-2 px-1 text-xs leading-relaxed text-muted-foreground"
							>
								Nessun suggerimento: puoi confermare questa descrizione come nuova lavorazione.
							</p>
						)}
				</div>
			</div>

			<button
				className="mb-3 w-full shrink-0 rounded-2xl bg-primary px-6 py-4 text-lg font-bold text-primary-foreground transition-colors active:scale-[0.97] disabled:opacity-50"
				disabled={!canConfirm}
				onClick={() => onConfirm(text.trim())}
			>
				<CheckCircle2 className="mr-2 inline h-5 w-5" />
				Conferma uscita
			</button>
			<button
				className="mb-auto w-full shrink-0 rounded-2xl bg-muted px-6 py-4 text-base font-medium text-muted-foreground transition-colors active:scale-[0.97]"
				onClick={onSkip}
			>
				Salta
			</button>
		</div>
	);
}

// ─── Rapportino Prompt (shown after uscita) ──────────────────

function RapportinoPrompt({
	target,
	onComplete,
	onSkip,
}: {
	target: Giornata | null;
	onComplete: () => void;
	onSkip: () => void;
}) {
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState('');
	const fileInputRef = useRef<HTMLInputElement>(null);

	async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		const allowed = ['image/jpeg', 'image/png', 'image/webp'];
		if (!allowed.includes(file.type)) {
			setError('Formato non supportato. Usa JPEG, PNG o WebP.');
			return;
		}
		if (file.size > 10 * 1024 * 1024) {
			setError('Foto troppo grande (max 10MB).');
			return;
		}

		setUploading(true);
		setError('');
		try {
			const compressed = await compressImage(file);
			await api.uploadDocument(compressed, 'rapportino', undefined, undefined, {
				data: target?.data,
				timbratura_id: target?.id,
			});
			onComplete();
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Errore caricamento. Riprova.');
		} finally {
			setUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = '';
		}
	}

	return (
		<div className="mx-auto flex h-dvh max-w-[420px] flex-col px-4 py-4">
			<div className="mt-auto mb-4 rounded-2xl bg-secondary p-6 text-center">
				<CheckCircle2 className="mx-auto mb-2 h-10 w-10 text-emerald-500" />
				<p className="text-lg font-bold text-foreground">Giornata terminata!</p>
				<p className="mt-2 text-sm text-muted-foreground">
					Ora carica il <strong>rapportino</strong> giornaliero.
				</p>
				{target?.data && (
					<p className="mt-1 text-sm font-semibold text-amber-600">
						Giornata: {formatDate(target.data)}
					</p>
				)}
				<p className="mt-1 text-xs text-muted-foreground">
					Scatta una foto del rapportino compilato.
				</p>
			</div>

			{error && (
				<div className="mb-3 rounded-xl bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700">
					{error}
				</div>
			)}

			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/png,image/webp"
				capture="environment"
				className="hidden"
				onChange={handleFile}
			/>

			<button
				className="mb-3 w-full rounded-2xl bg-primary px-6 py-5 text-xl font-bold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-50"
				disabled={uploading}
				onClick={() => fileInputRef.current?.click()}
			>
				{uploading ? (
					<>
						<Loader2 className="mr-2 inline h-5 w-5 animate-spin" /> Caricamento...
					</>
				) : (
					<>
						<Camera className="mr-2 inline h-5 w-5" /> SCATTA RAPPORTINO
					</>
				)}
			</button>

			<button
				className="mb-auto w-full rounded-2xl bg-muted px-6 py-3.5 text-base font-semibold text-muted-foreground transition-colors active:scale-[0.97]"
				disabled={uploading}
				onClick={onSkip}
			>
				Lo carico dopo
			</button>
		</div>
	);
}

const TIPI_DOC: { value: api.TipoDocumento; label: string; icon: typeof ClipboardList }[] = [
	{ value: 'rapportino', label: 'Rapportino', icon: ClipboardList },
	{ value: 'ddt', label: 'DDT', icon: Package },
	{ value: 'spesa', label: 'Scontrino / Spesa', icon: Receipt },
	{ value: 'carburante', label: 'Carburante', icon: Fuel },
];

/** Keep native picker cancellation and explicit cancellation on the same cleanup path. */
function pickFile(
	inputRef: React.RefObject<HTMLInputElement | null>,
	signal: AbortSignal,
): Promise<File | null> {
	return new Promise((resolve, reject) => {
		const input = inputRef.current;
		if (!input || signal.aborted) {
			resolve(null);
			return;
		}
		input.value = '';
		const cleanup = () => {
			input.removeEventListener('change', onChange);
			input.removeEventListener('cancel', onCancel);
			signal.removeEventListener('abort', onCancel);
		};
		const onChange = () => {
			const file = input.files?.[0] ?? null;
			cleanup();
			input.value = '';
			resolve(file);
		};
		const onCancel = () => {
			cleanup();
			input.value = '';
			resolve(null);
		};
		input.addEventListener('change', onChange);
		input.addEventListener('cancel', onCancel);
		signal.addEventListener('abort', onCancel);
		try {
			input.click();
		} catch (error) {
			cleanup();
			reject(error);
		}
	});
}

export function DocumentiSection({
	cantieri,
	sessioniAperte,
	onBusyChange,
	onToast,
}: {
	cantieri: Cantiere[];
	sessioniAperte: SessioneCantierePublic[];
	onBusyChange: (busy: boolean) => void;
	onToast: (type: 'ok' | 'err', msg: string) => void;
}) {
	const [operation, setOperation] = useState<'pick' | 'upload' | 'delete' | null>(null);
	const operationRef = useRef(false);
	const pickerAbortRef = useRef<AbortController | null>(null);
	const busy = operation !== null;
	useEffect(() => {
		return () => {
			pickerAbortRef.current?.abort();
			onBusyChange(false);
		};
	}, [onBusyChange]);
	const [myDocs, setMyDocs] = useState<api.DocumentoPublic[]>([]);
	const [loadingDocs, setLoadingDocs] = useState(true);
	const [docsError, setDocsError] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const retryInputRef = useRef<HTMLInputElement>(null);

	// Auto-select cantiere from open session (if exactly 1)
	const autoCantiereId = sessioniAperte.length === 1 ? sessioniAperte[0]?.cantiere_id : undefined;
	const [selectedCantiereId, setSelectedCantiereId] = useState<string>(autoCantiereId ?? '');

	useEffect(() => {
		loadMyDocs();
	}, []);

	async function loadMyDocs() {
		setLoadingDocs(true);
		setDocsError(false);
		try {
			const res = await api.getMyDocumentsToday();
			setMyDocs(res.data);
		} catch {
			setDocsError(true);
		} finally {
			setLoadingDocs(false);
		}
	}

	function beginOperation(next: 'pick' | 'delete') {
		if (operationRef.current) return false;
		operationRef.current = true;
		setOperation(next);
		onBusyChange(true);
		return true;
	}

	function endOperation() {
		pickerAbortRef.current = null;
		operationRef.current = false;
		setOperation(null);
		onBusyChange(false);
	}

	async function uploadPhoto(tipo: api.TipoDocumento, docId?: string) {
		if (!beginOperation('pick')) return;
		const controller = new AbortController();
		pickerAbortRef.current = controller;
		try {
			const file = await pickFile(docId ? retryInputRef : fileInputRef, controller.signal);
			if (!file) return;
			const allowed = ['image/jpeg', 'image/png', 'image/webp'];
			if (!allowed.includes(file.type)) {
				throw new Error('Formato non supportato. Usa JPEG, PNG o WebP.');
			}
			if (file.size > 10 * 1024 * 1024) {
				throw new Error('Foto troppo grande (max 10MB).');
			}

			setOperation('upload');
			const compressed = await compressImage(file);

			if (docId) {
				try {
					await api.deleteDocument(docId);
				} catch {
					/* best effort */
				}
			}
			await api.uploadDocument(compressed, tipo, selectedCantiereId || undefined);
			onToast('ok', `${TIPI_DOC.find((t) => t.value === tipo)?.label ?? 'Documento'} caricato!`);
			await loadMyDocs();
		} catch (err) {
			onToast(
				'err',
				err instanceof Error ? err.message : 'Errore durante il caricamento. Riprova.',
			);
		} finally {
			endOperation();
		}
	}

	function selectTipo(tipo: api.TipoDocumento) {
		if (operationRef.current) return;
		if (!selectedCantiereId && tipo !== 'rapportino') {
			onToast('err', 'Seleziona un cantiere prima di scattare la foto.');
			return;
		}
		void uploadPhoto(tipo);
	}

	function handleRetryUpload(docId: string) {
		const failedDoc = myDocs.find((d) => d.id === docId);
		if (!failedDoc) return;
		void uploadPhoto(failedDoc.tipo, docId);
	}

	async function handleDeleteDocument(docId: string) {
		if (!beginOperation('delete')) return;
		try {
			if (!confirm('Eliminare questo documento?')) return;
			await api.deleteDocument(docId);
			setMyDocs((prev) => prev.filter((d) => d.id !== docId));
			onToast('ok', 'Documento eliminato.');
		} catch (err) {
			onToast('err', err instanceof Error ? err.message : "Errore durante l'eliminazione.");
		} finally {
			endOperation();
		}
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col bg-background">
			{/* Header */}
			<div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3">
				<h2 className="flex items-center gap-2 text-lg font-bold text-foreground">
					<Camera className="h-5 w-5 text-primary" />
					Documenti
				</h2>
			</div>

			{/* Scrollable content */}
			<div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4">
				{/* Hidden file inputs */}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/jpeg,image/png,image/webp"
					capture="environment"
					className="hidden"
				/>
				<input
					ref={retryInputRef}
					type="file"
					accept="image/jpeg,image/png,image/webp"
					capture="environment"
					className="hidden"
				/>

				{/* Cantiere selector (obbligatorio) */}
				<div className="mb-4">
					<label
						htmlFor="document-site"
						className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground"
					>
						Cantiere *
					</label>
					<select
						id="document-site"
						className="w-full rounded-xl border-2 border-border bg-card px-3 py-3 text-sm font-semibold text-foreground"
						value={selectedCantiereId}
						disabled={busy}
						onChange={(e) => setSelectedCantiereId(e.target.value)}
					>
						<option value="">— Seleziona cantiere —</option>
						{cantieri.map((c) => (
							<option key={c.id} value={c.id}>
								{c.nome}
							</option>
						))}
					</select>
				</div>

				{/* Document type grid */}
				{busy ? (
					<div className="mb-4 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 px-3 py-4 text-center">
						<p role="status" className="font-semibold text-primary">
							<Loader2 className="mr-2 inline h-5 w-5 animate-spin" aria-hidden="true" />
							{operation === 'pick'
								? 'Scelta della foto…'
								: operation === 'delete'
									? 'Eliminazione in corso…'
									: 'Caricamento in corso…'}
						</p>
						{operation === 'pick' && (
							<button
								type="button"
								className="mt-2 min-h-11 rounded-lg px-3 font-semibold underline"
								onClick={() => pickerAbortRef.current?.abort()}
							>
								Annulla scelta foto
							</button>
						)}
					</div>
				) : (
					<div className="mb-4 grid grid-cols-2 gap-2">
						{TIPI_DOC.map((t) => {
							const needsCantiere = t.value !== 'rapportino';
							const isDisabled = needsCantiere && !selectedCantiereId;
							return (
								<button
									key={t.value}
									className={`flex flex-col items-center gap-1.5 rounded-xl border-2 bg-card px-3 py-4 text-sm font-semibold transition-colors active:scale-[0.97] ${isDisabled ? 'border-border/50 opacity-50' : 'border-border active:bg-accent'}`}
									onClick={() => selectTipo(t.value)}
									disabled={isDisabled}
								>
									<t.icon className="h-6 w-6 text-primary" />
									{t.label}
								</button>
							);
						})}
					</div>
				)}

				{/* Separator */}
				{myDocs.length > 0 && !docsError && (
					<p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
						Caricati oggi
					</p>
				)}

				{/* Today's documents list */}
				{docsError ? (
					<div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm">
						<p>Non riusciamo a caricare i documenti di oggi.</p>
						<button
							type="button"
							className="mt-2 min-h-11 font-semibold underline"
							disabled={busy}
							onClick={() => void loadMyDocs()}
						>
							Riprova caricamento documenti
						</button>
					</div>
				) : loadingDocs ? (
					<p className="text-center text-sm text-muted-foreground">Caricamento...</p>
				) : myDocs.length === 0 ? (
					<p className="text-center text-sm text-muted-foreground">Nessun documento oggi</p>
				) : (
					<div className="flex flex-col gap-2">
						{myDocs.map((d) => {
							const tipoInfo = TIPI_DOC.find((t) => t.value === d.tipo);
							const TipoIcon = tipoInfo?.icon ?? FileText;
							const uploadFailed = d.foto_urls.length === 0 && d.stato === 'ricevuto';
							const canDelete = d.stato === 'ricevuto';
							const ora = d.created_at
								? new Date(d.created_at).toLocaleTimeString('it-IT', {
										hour: '2-digit',
										minute: '2-digit',
									})
								: null;
							return (
								<div
									key={d.id}
									className={`relative flex flex-col gap-1 rounded-lg border bg-card px-3 py-2.5 ${uploadFailed ? 'border-amber-300' : 'border-border'}`}
								>
									<div className="flex w-full items-center">
										<span className="flex flex-1 items-center gap-2 font-semibold">
											<TipoIcon className="h-4 w-4 text-muted-foreground" />
											{tipoInfo?.label ?? d.tipo}
											{ora && (
												<span className="text-xs font-normal text-muted-foreground">{ora}</span>
											)}
										</span>
										<span
											className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
												d.stato === 'approvato'
													? 'bg-green-100 text-green-800'
													: d.stato === 'rifiutato'
														? 'bg-red-100 text-red-800'
														: uploadFailed
															? 'bg-amber-100 text-amber-800'
															: 'bg-green-50 text-green-700'
											}`}
										>
											{d.stato === 'approvato'
												? '✓ approvato'
												: d.stato === 'rifiutato'
													? '✗ rifiutato'
													: uploadFailed
														? '⚠ upload fallito'
														: '✓ caricato'}
										</span>
									</div>
									{uploadFailed && (
										<div className="mt-1 flex gap-2">
											<button
												type="button"
												className="flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors active:opacity-80 disabled:opacity-50"
												disabled={busy}
												onClick={() => handleRetryUpload(d.id)}
											>
												<RefreshCw className="h-3.5 w-3.5" />
												Riprova
											</button>
											<button
												type="button"
												className="flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-lg bg-destructive/10 px-3 py-2 text-xs font-semibold text-destructive transition-colors active:opacity-80 disabled:opacity-50"
												disabled={busy}
												onClick={() => handleDeleteDocument(d.id)}
											>
												<Trash2 className="h-3.5 w-3.5" />
												Elimina
											</button>
										</div>
									)}
									{d.foto_urls.length > 0 && (
										<div className="relative mt-1">
											<img
												src={d.foto_urls[0]}
												alt={d.tipo}
												className="w-full max-h-[120px] rounded-lg object-cover"
											/>
											{canDelete && !uploadFailed && (
												<button
													type="button"
													className="absolute top-1 right-1 flex h-11 w-11 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors active:bg-black/70 disabled:opacity-50"
													disabled={busy}
													onClick={() => handleDeleteDocument(d.id)}
													aria-label="Elimina documento"
												>
													<Trash2 className="h-3.5 w-3.5" />
												</button>
											)}
										</div>
									)}
									{d.nota_dipendente && (
										<p className="text-xs text-muted-foreground">{d.nota_dipendente}</p>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}

// ─── Tesserino Digitale ──────────────────────────────────────

function TesserinoScreen({ onClose }: { onClose: () => void }) {
	const modalRef = useModalFocus(onClose);
	const [data, setData] = useState<api.TesserinoData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const dipId = api.getDipendenteId();
		if (!dipId) {
			setError('ID dipendente non disponibile');
			setLoading(false);
			return;
		}
		api
			.getTesserinoData(dipId)
			.then((d) => {
				setData(d);
				// Cache for offline
				try {
					localStorage.setItem('tesserino_cache', JSON.stringify(d));
				} catch {
					/* quota */
				}
			})
			.catch(() => {
				// Try cached version
				try {
					const cached = localStorage.getItem('tesserino_cache');
					if (cached) {
						setData(JSON.parse(cached));
						return;
					}
				} catch {
					/* corrupt */
				}
				setError('Impossibile caricare il tesserino');
			})
			.finally(() => setLoading(false));
	}, []);

	// Status helpers
	function getExpiryStatus(dateStr: string | null): 'valid' | 'expiring' | 'expired' | 'unknown' {
		if (!dateStr) return 'unknown';
		const today = new Date();
		const expiry = new Date(dateStr);
		const diffDays = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
		if (diffDays < 0) return 'expired';
		if (diffDays <= 30) return 'expiring';
		return 'valid';
	}

	function statusColor(status: 'valid' | 'expiring' | 'expired' | 'unknown') {
		switch (status) {
			case 'valid':
				return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
			case 'expiring':
				return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
			case 'expired':
				return 'bg-red-500/20 text-red-300 border-red-500/30';
			default:
				return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
		}
	}

	function statusLabel(status: 'valid' | 'expiring' | 'expired' | 'unknown') {
		switch (status) {
			case 'valid':
				return 'Valido';
			case 'expiring':
				return 'In scadenza';
			case 'expired':
				return 'Scaduto';
			default:
				return '—';
		}
	}

	function formatDate(d: string | null): string {
		if (!d) return '—';
		return new Date(d).toLocaleDateString('it-IT', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		});
	}

	const verificaUrl = data ? api.getVerificaUrl(data.verifica_token) : '';
	const qrSvg = verificaUrl ? generateQrSvg(verificaUrl, 3, 1) : '';

	const [openingAllegato, setOpeningAllegato] = useState<string | null>(null);
	const [copiedLink, setCopiedLink] = useState(false);

	async function openAllegato(type: 'formazione' | 'idoneita', id: string) {
		setOpeningAllegato(id);
		// Open window immediately (user gesture) to avoid popup blocker
		const w = window.open('about:blank', '_blank');
		try {
			const url =
				type === 'formazione'
					? await api.getFormazioneAllegatoUrl(id)
					: await api.getIdoneitaAllegatoUrl(id);
			if (w) {
				w.location.href = url;
			} else {
				// Fallback: navigate in same tab
				window.location.href = url;
			}
		} catch {
			if (w) w.close();
		} finally {
			setOpeningAllegato(null);
		}
	}

	// Idoneita status
	const idoneitaStatus = data?.idoneita ? getExpiryStatus(data.idoneita.data_scadenza) : 'unknown';
	// Formazione: worst status among all courses
	const formazioneStatus = data?.formazione.length
		? data.formazione.reduce<'valid' | 'expiring' | 'expired' | 'unknown'>((worst, f) => {
				const s = getExpiryStatus(f.data_scadenza);
				if (s === 'expired') return 'expired';
				if (s === 'expiring' && worst !== 'expired') return 'expiring';
				return worst;
			}, 'valid')
		: 'unknown';

	return (
		<div
			ref={modalRef}
			role="dialog"
			aria-modal="true"
			aria-labelledby="badge-title"
			tabIndex={-1}
			className="overlay-enter safe-top safe-x safe-bottom fixed inset-0 z-40 flex flex-col bg-slate-950"
		>
			{/* Header bar */}
			<div className="relative flex shrink-0 items-center justify-center px-4 py-3">
				<h2 id="badge-title" className="flex items-center gap-2 text-base font-bold text-white">
					<Shield className="h-4 w-4 text-blue-400" />
					Tesserino Digitale
				</h2>
				<button
					type="button"
					aria-label="Torna al profilo"
					className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full transition-colors active:bg-white/10"
					onClick={onClose}
				>
					<X className="h-5 w-5 text-slate-400" />
				</button>
			</div>

			{/* Content */}
			<div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-4 pb-8 pt-2">
				{loading && (
					<div className="flex flex-1 items-center justify-center">
						<Loader2 className="h-8 w-8 animate-spin text-blue-400" />
					</div>
				)}
				{error && (
					<div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
						<AlertTriangle className="h-10 w-10 text-amber-400" />
						<p className="text-sm text-slate-300">{error}</p>
					</div>
				)}
				{data && !loading && (
					<>
						{/* Main card */}
						<div className="w-full max-w-sm">
							{/* Card face */}
							<div className="relative overflow-hidden rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800 via-slate-850 to-slate-900 shadow-2xl">
								{/* Decorative top accent */}
								<div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

								{/* Header */}
								<div className="px-5 pt-5 pb-3">
									<div className="flex items-start justify-between">
										<div>
											<p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
												Tesserino di Riconoscimento
											</p>
											<p className="mt-0.5 text-[9px] text-slate-600">Art. 26 c.8 D.Lgs. 81/2008</p>
										</div>
										<ShieldCheck className="h-5 w-5 text-blue-500/60" />
									</div>
									<p className="mt-2 text-sm font-bold text-white">{data.azienda_nome}</p>
								</div>

								{/* Divider */}
								<div className="mx-5 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent" />

								{/* Body — photo + data */}
								<div className="flex gap-4 px-5 py-4">
									{/* Photo */}
									<div className="shrink-0">
										{data.foto_url ? (
											<img
												src={data.foto_url}
												alt="Foto profilo"
												className="h-24 w-20 rounded-lg border border-slate-600 object-cover shadow-lg"
											/>
										) : (
											<div className="flex h-24 w-20 items-center justify-center rounded-lg border border-slate-600 bg-slate-700">
												<HardHat className="h-8 w-8 text-slate-500" />
											</div>
										)}
									</div>

									{/* Data */}
									<div className="flex min-w-0 flex-1 flex-col gap-1.5">
										<div>
											<p className="text-xs uppercase tracking-wide text-slate-500">
												Cognome e Nome
											</p>
											<p className="truncate text-sm font-bold text-white">
												{data.cognome} {data.nome}
											</p>
										</div>
										{data.codice_fiscale && (
											<div>
												<p className="text-xs uppercase tracking-wide text-slate-500">
													Codice Fiscale
												</p>
												<p className="font-mono text-xs text-slate-200">{data.codice_fiscale}</p>
											</div>
										)}
										{data.data_nascita && (
											<div>
												<p className="text-xs uppercase tracking-wide text-slate-500">
													Data nascita
												</p>
												<p className="text-xs text-slate-300">
													{formatDate(data.data_nascita)}
													{data.luogo_nascita && ` — ${data.luogo_nascita}`}
												</p>
											</div>
										)}
										{data.mansione && (
											<div>
												<p className="text-xs uppercase tracking-wide text-slate-500">Mansione</p>
												<p className="text-xs font-medium text-slate-200">{data.mansione}</p>
											</div>
										)}
										{data.data_assunzione && (
											<div>
												<p className="text-xs uppercase tracking-wide text-slate-500">
													Data assunzione
												</p>
												<p className="text-xs text-slate-300">{formatDate(data.data_assunzione)}</p>
											</div>
										)}
									</div>
								</div>

								{/* Status badges */}
								<div className="mx-5 mb-4 flex gap-2">
									<div
										className={`flex flex-1 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 ${statusColor(formazioneStatus)}`}
									>
										<div
											className={`h-1.5 w-1.5 rounded-full ${formazioneStatus === 'valid' ? 'bg-emerald-400' : formazioneStatus === 'expiring' ? 'bg-amber-400' : formazioneStatus === 'expired' ? 'bg-red-400' : 'bg-slate-500'}`}
										/>
										<span className="text-xs font-semibold">Formazione</span>
										<span className="ml-auto text-[9px] opacity-80">
											{statusLabel(formazioneStatus)}
										</span>
									</div>
									<div
										className={`flex flex-1 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 ${statusColor(idoneitaStatus)}`}
									>
										<div
											className={`h-1.5 w-1.5 rounded-full ${idoneitaStatus === 'valid' ? 'bg-emerald-400' : idoneitaStatus === 'expiring' ? 'bg-amber-400' : idoneitaStatus === 'expired' ? 'bg-red-400' : 'bg-slate-500'}`}
										/>
										<span className="text-xs font-semibold">Idoneità</span>
										<span className="ml-auto text-[9px] opacity-80">
											{statusLabel(idoneitaStatus)}
										</span>
									</div>
								</div>

								{/* QR Code section */}
								<div className="border-t border-slate-700/50 bg-white/[0.02] px-5 py-4">
									<div className="flex items-center gap-4">
										<div
											className="shrink-0 rounded-lg bg-white p-1.5 shadow-lg"
											dangerouslySetInnerHTML={{ __html: qrSvg }}
										/>
										<div className="min-w-0 flex-1">
											<p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
												Verifica autenticità
											</p>
											<p className="mt-0.5 text-[9px] text-slate-500 leading-relaxed">
												Scansiona il QR code o usa i pulsanti per verificare l'identità del
												lavoratore.
											</p>
											<div className="mt-2 flex gap-2">
												<button
													type="button"
													className="flex items-center gap-1 rounded-md bg-slate-700/60 px-2.5 py-1 text-xs font-medium text-slate-300 transition-colors active:bg-slate-600"
													onClick={async () => {
														await navigator.clipboard.writeText(verificaUrl);
														setCopiedLink(true);
														setTimeout(() => setCopiedLink(false), 2000);
													}}
												>
													{copiedLink ? (
														<>
															<CheckCircle2 className="h-3 w-3 text-emerald-400" /> Copiato!
														</>
													) : (
														<>
															<ClipboardList className="h-3 w-3" /> Copia link
														</>
													)}
												</button>
												<button
													type="button"
													className="flex items-center gap-1 rounded-md bg-blue-500/20 px-2.5 py-1 text-xs font-medium text-blue-300 transition-colors active:bg-blue-500/30"
													onClick={() => window.open(verificaUrl, '_blank')}
												>
													<Share2 className="h-3 w-3" /> Apri
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Detail cards below */}
						{(data.formazione.length > 0 || data.idoneita) && (
							<div className="mt-5 w-full max-w-sm space-y-3">
								{/* Formazione detail */}
								{data.formazione.length > 0 && (
									<div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4">
										<h3 className="mb-3 flex items-center gap-1.5 text-xs font-bold text-slate-300">
											<ShieldCheck className="h-3.5 w-3.5 text-blue-400" />
											Formazione
										</h3>
										<div className="space-y-3">
											{data.formazione.map((f, i) => {
												const st = getExpiryStatus(f.data_scadenza);
												return (
													<div
														key={i}
														className="rounded-lg border border-slate-700/30 bg-slate-800/60 px-3 py-2.5"
													>
														<div className="flex items-center justify-between gap-2">
															<span className="text-xs font-medium text-slate-200">
																{f.tipo_corso}
															</span>
															<span
																className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
																	st === 'valid'
																		? 'bg-emerald-500/20 text-emerald-300'
																		: st === 'expiring'
																			? 'bg-amber-500/20 text-amber-300'
																			: st === 'expired'
																				? 'bg-red-500/20 text-red-300'
																				: 'bg-slate-600 text-slate-400'
																}`}
															>
																{f.data_scadenza ? formatDate(f.data_scadenza) : 'Nessuna scadenza'}
															</span>
														</div>
														<div className="mt-1.5">
															{f.has_allegato ? (
																<button
																	type="button"
																	className="flex h-7 items-center gap-1.5 rounded-md bg-blue-500/20 px-3 text-xs font-medium text-blue-300 transition-colors active:bg-blue-500/30"
																	onClick={() => openAllegato('formazione', f.id)}
																	disabled={openingAllegato === f.id}
																>
																	{openingAllegato === f.id ? (
																		<Loader2 className="h-3.5 w-3.5 animate-spin" />
																	) : (
																		<FileText className="h-3.5 w-3.5" />
																	)}
																	Visualizza attestato
																</button>
															) : (
																<p className="text-xs text-slate-600 italic">
																	Nessun attestato caricato
																</p>
															)}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								)}

								{/* Idoneita detail */}
								{data.idoneita && (
									<div className="rounded-xl border border-slate-700/50 bg-slate-800/50 p-4">
										<h3 className="mb-3 flex items-center gap-1.5 text-xs font-bold text-slate-300">
											<ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
											Idoneità Sanitaria
										</h3>
										<div className="rounded-lg border border-slate-700/30 bg-slate-800/60 px-3 py-2.5">
											<div className="flex items-center justify-between gap-2">
												<span className="text-xs font-medium text-slate-200 capitalize">
													{data.idoneita.esito.replace(/_/g, ' ')}
												</span>
												<span
													className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
														idoneitaStatus === 'valid'
															? 'bg-emerald-500/20 text-emerald-300'
															: idoneitaStatus === 'expiring'
																? 'bg-amber-500/20 text-amber-300'
																: idoneitaStatus === 'expired'
																	? 'bg-red-500/20 text-red-300'
																	: 'bg-slate-600 text-slate-400'
													}`}
												>
													{data.idoneita.data_scadenza
														? `Scade ${formatDate(data.idoneita.data_scadenza)}`
														: 'Nessuna scadenza'}
												</span>
											</div>
											<p className="mt-0.5 text-xs text-slate-500">
												Visita: {formatDate(data.idoneita.data_visita)}
											</p>
											<div className="mt-1.5">
												{data.idoneita.has_allegato ? (
													<button
														type="button"
														className="flex h-7 items-center gap-1.5 rounded-md bg-blue-500/20 px-3 text-xs font-medium text-blue-300 transition-colors active:bg-blue-500/30"
														onClick={() => openAllegato('idoneita', data.idoneita!.id)}
														disabled={openingAllegato === data.idoneita!.id}
													>
														{openingAllegato === data.idoneita!.id ? (
															<Loader2 className="h-3.5 w-3.5 animate-spin" />
														) : (
															<FileText className="h-3.5 w-3.5" />
														)}
														Visualizza certificato
													</button>
												) : (
													<p className="text-xs text-slate-600 italic">
														Nessun certificato caricato
													</p>
												)}
											</div>
										</div>
									</div>
								)}
							</div>
						)}

						{/* Action buttons */}
						<div className="mt-5 flex w-full max-w-sm gap-3">
							<button
								type="button"
								className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/80 px-4 py-3 text-sm font-semibold text-slate-200 transition-colors active:bg-slate-700"
								onClick={() => {
									const dipId = api.getDipendenteId();
									if (dipId) {
										window.open(api.getTesserinoUrl(dipId), '_blank');
									}
								}}
							>
								<Download className="h-4 w-4" />
								Scarica PDF
							</button>
							<button
								type="button"
								className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-blue-600/50 bg-blue-600/20 px-4 py-3 text-sm font-semibold text-blue-300 transition-colors active:bg-blue-600/30"
								onClick={async () => {
									if (!data) return;
									const shareData = {
										title: `Tesserino - ${data.cognome} ${data.nome}`,
										text: `Tesserino di riconoscimento\n${data.cognome} ${data.nome}\n${data.azienda_nome}\nMansione: ${data.mansione ?? 'N/D'}\n\nVerifica autenticità:`,
										url: verificaUrl,
									};
									if (navigator.share) {
										try {
											await navigator.share(shareData);
										} catch {
											/* user cancelled */
										}
									} else {
										// Fallback: copy to clipboard
										await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
									}
								}}
							>
								<Share2 className="h-4 w-4" />
								Condividi
							</button>
						</div>

						{/* Footer note */}
						<p className="mt-4 max-w-sm text-center text-xs text-slate-600">
							Documento digitale generato da OreInCantiere.
							<br />
							Validità verificabile tramite QR code.
						</p>
					</>
				)}
			</div>
		</div>
	);
}

// ─── Auth Screen (token-link) ────────────────────────────────

function AuthScreen({ onAuth, sessionMessage }: { onAuth: () => void; sessionMessage?: string }) {
	const [token, setToken] = useState('');
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const [autoAuthInProgress, setAutoAuthInProgress] = useState(false);

	// Check URL for token parameter on mount — supports both /t/:token path and ?token= query
	useEffect(() => {
		const pathMatch = window.location.pathname.match(/^\/t\/(.+)$/);
		const urlToken = pathMatch?.[1] ?? new URLSearchParams(window.location.search).get('token');
		if (urlToken) {
			setAutoAuthInProgress(true);
			doAuth(urlToken);
		}
	}, []);

	async function doAuth(pwaToken: string) {
		setLoading(true);
		setError('');
		try {
			const res = await api.pwaAuth(pwaToken);
			api.setToken(res.accessToken);
			api.setRefreshToken(res.refreshToken);
			if (res.nome) api.setUserNome(res.nome);
			if (res.dipendente_id) api.setDipendenteId(res.dipendente_id);
			// Clean URL
			window.history.replaceState({}, '', '/');
			onAuth();
		} catch (e) {
			setAutoAuthInProgress(false);
			if (e instanceof api.ApiError) {
				setError(e.message);
			} else {
				setError('Impossibile verificare il codice. Riprova o contatta il tuo responsabile.');
			}
		} finally {
			setLoading(false);
		}
	}

	// Full-screen loading while auto-authenticating from URL token
	if (autoAuthInProgress && loading) {
		return (
			<div className="mx-auto flex h-dvh max-w-[420px] flex-col items-center justify-center px-4">
				<div className="flex items-center gap-2">
					<HardHat className="h-7 w-7 text-primary" />
					<h1 className="text-2xl font-bold text-primary">Ore in Cantiere</h1>
				</div>
				<p className="mt-4 text-center text-muted-foreground">
					<Loader2 className="mr-1 inline h-4 w-4 animate-spin" />
					Accesso in corso...
				</p>
			</div>
		);
	}

	return (
		<div className="mx-auto flex h-dvh max-w-[420px] flex-col justify-center px-4">
			<div className="mb-6 flex items-center justify-center gap-2">
				<HardHat className="h-7 w-7 text-primary" />
				<h1 className="text-2xl font-bold text-primary">Ore in Cantiere</h1>
			</div>

			{sessionMessage && (
				<div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4 text-center">
					<p className="mb-1 font-semibold text-red-800">
						<AlertTriangle className="mr-1 inline h-4 w-4" />
						Sessione scaduta
					</p>
					<p className="text-sm leading-relaxed text-red-900">{sessionMessage}</p>
				</div>
			)}

			<p className="mb-8 text-center text-sm text-muted-foreground">
				Usa il link ricevuto su WhatsApp oppure inserisci il codice
			</p>

			<div className="relative mb-4">
				<KeyRound className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
				<input
					className="w-full rounded-xl border border-border bg-card py-3 pl-10 pr-4 text-base outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
					type="text"
					placeholder="Codice accesso"
					value={token}
					onChange={(e) => setToken(e.target.value)}
					disabled={loading}
				/>
			</div>

			<button
				className="w-full rounded-xl bg-primary py-3.5 text-base font-bold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-50"
				onClick={() => doAuth(token)}
				disabled={loading || !token.trim()}
			>
				{loading ? (
					<>
						<Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Verifica...
					</>
				) : (
					'Accedi'
				)}
			</button>

			{error && (
				<div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-center text-sm text-red-800">
					{error}
				</div>
			)}
		</div>
	);
}

// ─── Helpers ─────────────────────────────────────────────────

function formatDate(iso: string): string {
	const d = new Date(iso + 'T00:00:00');
	return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatTime(iso: string | undefined): string {
	if (!iso) return '--:--';
	return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function elapsedSince(iso: string | undefined): string {
	if (!iso) return '';
	const diff = Date.now() - new Date(iso).getTime();
	const h = Math.floor(diff / 3_600_000);
	const m = Math.floor((diff % 3_600_000) / 60_000);
	return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
